import { ExportProgressReporter } from "../../components/CustomButton";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Select, MenuItem, Tooltip } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import CustomLabel from "../../components/CustomLabel";
import PageHeader from "../../components/PageHeader";
import GenericFormDialog, { Field } from "../../components/GenericFormDialog";
import CustomDialog from "../../components/CustomDialog";
import CustomSelect from "../../components/CustomSelect";
import CustomTable from "../../components/table/CustomTable";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import CustomTextField from "../../components/CustomTextField";
import SearchHeaderContainer from "../../components/table/SearchHeaderContainer";
import Actions from "../../components/Actions";
import ConfirmDialog from "../../components/ConfirmDialog";
import { COLORS } from "../../assets/theme";
import CustomButton from "../../components/CustomButton";
import InvoiceItemsEditor, {
  InvoiceItem,
  isInvoiceProductMissingSelection,
} from "../../components/invoices/InvoiceItemsEditor";
import { getAllBranches } from "../../services/BranchesService";
import {
  createQuotation,
  getInvoicesByBusiness,
  getInvoicesByUnit,
  payInvoice,
  approveQuotation,
  getInvoiceById,
  cancelInvoice,
  refundInvoice,
  rejectQuotation,
  updateQuotation,
} from "../../services/newInvoicesService";
import { getClientById, getClientsSelect } from "../../services/ClientsService";
import {
  getAllServices,
  getAllServicesByBusiness,
} from "../../services/ServicesService";
import { fetchProducts } from "../../services/ProductsNewService";
import { fetchStockSummary } from "../../services/StocksNewService";
import { getRewards, LoyaltyReward } from "../../services/LoyaltyService";
import {
  createCreditNote,
  createDebitNote,
} from "../../services/newNotesService";
import { formatTableDate, formatTableDateTime } from "../../utils/tableDateFormat";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import {
  hasInvoiceSuccessfulPayment,
  InvoiceRewardPackageField,
  LoyaltyInvoicePreview,
} from "../Appointment";

const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const showErrorToast = (message: any) =>
  toast.error(String(message), { toastId: String(message) });

const toTitleCase = (value: string): string =>
  value
    .toLowerCase()
    .split(/\s+/)
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : ""))
    .join(" ");

const PAYMENT_METHOD_LABEL_MAP: Record<0 | 1 | 2 | 3 | 4, string> = {
  0: "Cash",
  1: "Card",
  2: "Cash",
  3: "Bank Transfer",
  4: "Online Payment",
};

const resolveInvoicePaidAt = (entry: any): string => {
  const invoice = entry?.invoice ?? {};
  const latestPayment = Array.isArray(entry?.payments)
    ? entry.payments[entry.payments.length - 1]
    : Array.isArray(invoice?.payments)
    ? invoice.payments[invoice.payments.length - 1]
    : null;

  return (
    entry?.paid_at ||
    entry?.paidAt ||
    entry?.payment_date ||
    entry?.paymentDate ||
    invoice?.paid_at ||
    invoice?.paidAt ||
    latestPayment?.paid_at ||
    latestPayment?.paidAt ||
    latestPayment?.created_at ||
    latestPayment?.createdAt ||
    ""
  );
};

type InvoiceClientOption = {
  id: string;
  name: string;
  email?: string;
  mobile?: string;
};

const pickNonEmptyString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
      return String(value);
    }
  }
  return undefined;
};

const getInvoiceMetadata = (entry: any) =>
  (entry?.metadata && typeof entry.metadata === "object" && entry.metadata) ||
  (entry?.invoice?.metadata &&
    typeof entry.invoice.metadata === "object" &&
    entry.invoice.metadata) ||
  {};

const getInvoiceMetadataClient = (entry: any) => {
  const metadata = getInvoiceMetadata(entry);
  return metadata?.client && typeof metadata.client === "object"
    ? metadata.client
    : {};
};

const resolveInvoiceClientId = (entry: any) => {
  const metadata = getInvoiceMetadata(entry);
  return (
    entry?.clientId ||
    entry?.client?.id ||
    entry?.invoice?.client_id ||
    entry?.client_id ||
    metadata?.clientOrgUserId ||
    metadata?.client_org_user_id ||
    ""
  );
};

const resolveInvoiceClientName = (entry: any) => {
  const metadataClient = getInvoiceMetadataClient(entry);
  return (
    pickNonEmptyString(
      entry?.clientName,
      entry?.client?.full_name,
      entry?.client?.fullName,
      entry?.client?.name,
      entry?.invoice?.clientName,
      entry?.invoice?.client?.full_name,
      entry?.invoice?.client?.fullName,
      entry?.invoice?.client?.name,
      metadataClient?.name,
      entry?.client_name
    ) || "N/A"
  );
};

const resolveInvoiceClientPhone = (entry: any) => {
  const metadataClient = getInvoiceMetadataClient(entry);
  return (
    pickNonEmptyString(
      entry?.clientPhone,
      entry?.client?.phone,
      entry?.client?.mobile,
      entry?.invoice?.clientPhone,
      entry?.invoice?.client?.phone,
      entry?.invoice?.client?.mobile,
      metadataClient?.phone,
      metadataClient?.mobile,
      entry?.mobile,
      entry?.phone
    ) || "N/A"
  );
};

const resolveInvoiceClientEmail = (entry: any) => {
  const metadataClient = getInvoiceMetadataClient(entry);
  return (
    pickNonEmptyString(
      entry?.email,
      entry?.client?.user?.email,
      entry?.client?.email,
      entry?.invoice?.email,
      entry?.invoice?.client?.user?.email,
      entry?.invoice?.client?.email,
      metadataClient?.email
    ) || ""
  );
};

// The branch select mixes two scopes: "all" spans the whole business, this
// sentinel narrows to invoices raised on the business itself rather than on any
// branch, and every other value is a unit id.
const BUSINESS_SCOPE_VALUE = "business";
const INVOICES_PAGE_SIZE = 30;

type InvoicesSummary = {
  count: number;
  totalAmount: number;
  paidAmount: number;
  remainingAmount: number;
};

// Products beyond this stay in the hover tooltip so the row keeps its height.
const PRODUCTS_SHOWN_IN_CELL = 2;

// "watert" x1 reads as "1 watert"; quantity first matches how staff say it.
const formatProductEntry = (product: any) => {
  const name = String(product?.name || product?.description || "").trim();
  if (!name) return "";
  const quantity = Number(product?.qty ?? product?.quantity ?? 1);
  const safeQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  return `${safeQuantity} ${name}`;
};

const toProductEntries = (item: any): string[] => {
  const list = Array.isArray(item?.products)
    ? item.products
    : Array.isArray(item?.invoice?.products)
      ? item.invoice.products
      : [];
  return list.map(formatProductEntry).filter(Boolean);
};

// Callers that need a unit id must not be handed a scope sentinel.
const toUnitId = (value: unknown) => {
  const selected = Array.isArray(value) ? "" : String(value ?? "");
  return selected && selected !== "all" && selected !== BUSINESS_SCOPE_VALUE
    ? selected
    : "";
};

const Invoices: React.FC = () => {
  const navigate = useNavigate();

  const params = useParams<{ businessId?: string; id?: string }>();
  const venueId = params.businessId || params.id;

  const [noteOpen, setNoteOpen] = useState(false);
  const [noteType, setNoteType] = useState<"CREDIT_NOTE" | "DEBIT_NOTE">(
    "CREDIT_NOTE"
  );
  const [noteInvoiceId, setNoteInvoiceId] = useState<number | null>(null);
  const [partialAmount, setPartialAmount] = useState<string>("");

  const [addOpen, setAddOpen] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [toDeleteId, setToDeleteId] = React.useState<string | number | null>(
    null
  );
  const [selectValue, setSelectValue] = React.useState<
    string | number | (string | number)[]
  >("");
  const [activeBranch, setActiveBranch] = React.useState<string | null>(null);
  const [branchList, setBranchList] = React.useState<string[]>([]);

  const [branches, setBranches] = React.useState<any[]>([]);
  const [invoices, setInvoices] = React.useState<any[]>([]);
  const [services, setServices] = React.useState<any[]>([]);
  const [rewardServices, setRewardServices] = React.useState<any[]>([]);
  const [clients, setClients] = React.useState<InvoiceClientOption[]>([]);
  const [products, setProducts] = React.useState<any[]>([]);
  const [stockItems, setStockItems] = React.useState<any[]>([]);
  const [loyaltyRewards, setLoyaltyRewards] = React.useState<LoyaltyReward[]>([]);
  const [loadingLoyaltyRewards, setLoadingLoyaltyRewards] =
    React.useState(false);
  const [invoicesLoading, setInvoicesLoading] = React.useState<boolean>(false);
  const [search, setSearch] = React.useState<string>("");
  const getTodayYmdLocal = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const roundInvoiceMoney = (value: number) =>
    Math.round((Number(value) || 0) * 100) / 100;
  const pickFiniteNumber = (...values: any[]) => {
    for (const value of values) {
      if (value === undefined || value === null || value === "") continue;
      const numeric = Number(value);
      if (Number.isFinite(numeric)) return numeric;
    }
    return undefined;
  };
  const calculateInvoiceItemTotal = (
    quantity: number,
    unitCost: number,
    discountPercent: number
  ) => {
    const subtotal = (Number(quantity) || 0) * (Number(unitCost) || 0);
    const discountAmount =
      (subtotal * Math.min(100, Math.max(0, Number(discountPercent) || 0))) /
      100;
    return roundInvoiceMoney(subtotal - discountAmount);
  };
  const resolveInvoiceItemDiscountPercent = (
    item: any,
    quantity: number,
    unitCost: number,
    totalPrice: number,
    promoType?: string,
    promoValue?: number
  ) => {
    const explicitPercentValue =
      item?.discountPercent ??
      item?.discount_percent ??
      item?.metadata?.discountPercent ??
      item?.metadata?.discount_percent;
    const explicitPercent = Number(explicitPercentValue);
    if (
      explicitPercentValue !== undefined &&
      explicitPercentValue !== null &&
      explicitPercentValue !== "" &&
      Number.isFinite(explicitPercent) &&
      explicitPercent > 0
    ) {
      return Math.min(100, Math.max(0, explicitPercent));
    }

    const subtotal = (Number(quantity) || 0) * (Number(unitCost) || 0);
    const normalizedPromoType = String(promoType || "").trim().toUpperCase();
    const numericPromoValue = Number(promoValue);
    if (
      subtotal > 0 &&
      Number.isFinite(numericPromoValue) &&
      numericPromoValue > 0
    ) {
      if (
        normalizedPromoType === "PERCENT" ||
        normalizedPromoType === "PERCENTAGE" ||
        normalizedPromoType === "DISCOUNT"
      ) {
        return Math.min(100, Math.max(0, numericPromoValue));
      }
      if (normalizedPromoType === "FLAT" || normalizedPromoType === "FIXED") {
        const lineDiscountAmount = Number(item?.discount);
        const flatDiscountAmount =
          Number.isFinite(lineDiscountAmount) && lineDiscountAmount > 0
            ? lineDiscountAmount
            : numericPromoValue;
        return roundInvoiceMoney(
          Math.min(100, Math.max(0, (flatDiscountAmount / subtotal) * 100))
        );
      }
    }

    if (subtotal > 0 && Number.isFinite(totalPrice)) {
      return roundInvoiceMoney(
        (Math.max(subtotal - totalPrice, 0) / subtotal) * 100
      );
    }

    return Math.min(100, Math.max(0, Number(item?.discount) || 0));
  };
  const getInvoiceItemDiscountAmount = (item: any) => {
    const subtotal = (Number(item?.quantity) || 0) * (Number(item?.unitCost) || 0);
    const totalPrice = Number(item?.totalPrice);

    if (Number.isFinite(totalPrice)) {
      return roundInvoiceMoney(Math.max(subtotal - totalPrice, 0));
    }

    return roundInvoiceMoney(
      (subtotal * Math.min(100, Math.max(0, Number(item?.discount) || 0))) / 100
    );
  };
  const [dateRange, setDateRange] = React.useState<
    { start?: string; end?: string } | [string?, string?] | null
  >(() => {
    const today = getTodayYmdLocal();
    return { start: today, end: today };
  });
  const [statusFilter, setStatusFilter] = React.useState<string>("all");
  const [paymentConfirmOpen, setPaymentConfirmOpen] = React.useState(false);
  const [toPayId, setToPayId] = React.useState<string | number | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = React.useState(false);
  const [selectedInvoice, setSelectedInvoice] = React.useState<any>(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = React.useState(false);
  const [toCancelId, setToCancelId] = React.useState<string | number | null>(
    null
  );
  const [cancelReason, setCancelReason] = React.useState<string>("");
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundInvoiceId, setRefundInvoiceId] = useState<number | null>(null);
  const [invoiceFormUnitId, setInvoiceFormUnitId] = React.useState<string>("");

  const [editingRow, setEditingRow] = React.useState<any | null>();
const [editOpen, setEditOpen] = React.useState(false);

  // Pagination & Server-side filtering
  const [currentPage, setCurrentPage] = React.useState<number>(0);
  const [pageSize, setPageSize] = React.useState<number>(20);
  const [totalElements, setTotalElements] = React.useState<number>(0);
  const [totalPages, setTotalPages] = React.useState<number>(0);

  const getInvoiceId = (inv: any) =>
    inv?.invoiceId ?? inv?.id ?? inv?.invoice_id ?? inv?.invoice?.id ?? null;

  const STATUS_LABELS: Record<string, string> = {
    PENDING: "Pending",
    PARTIALLY_PAID: "Partially Paid",
    PAID: "Paid",
    PARTIALLY_REFUNDED: "Partially Refunded",
    REFUNDED: "Refunded",
    APPROVED: "Approved",
    VOID: "Cancelled",
  };

  const businessDisplayName = React.useMemo(() => {
    const firstBranch = Array.isArray(branches) && branches.length > 0 ? branches[0] : null;
    return (
      pickNonEmptyString(
        firstBranch?.business_name,
        firstBranch?.businessName,
        firstBranch?.business?.name,
        firstBranch?.venue_name,
        firstBranch?.venueName,
        firstBranch?.organization_name,
        firstBranch?.organizationName
      ) || (venueId ? `Business ${venueId}` : "Business")
    );
  }, [branches, venueId]);

const extractRowDate = (row: any): Date | null => {
  const candidates = [
    row?.dueAt,
    row?.due_at,
    row?.invoice?.dueAt,
    row?.invoice?.due_at,
    row?.appointment?.start_at,
    row?.invoice_date,
    row?.created_at,
    row?.createdAt,
  ];
  for (const val of candidates) {
    if (!val) continue;
    const d = new Date(val);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
};

const buildDateBoundary = (raw: string | Date | null | undefined, endOfDay: boolean) => {
  if (!raw) return null;

  if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split("-").map(Number);
    return new Date(
      Date.UTC(
        y,
        m - 1,
        d,
        endOfDay ? 23 : 0,
        endOfDay ? 59 : 0,
        endOfDay ? 59 : 0,
        endOfDay ? 999 : 0
      )
    );
  }

  const parsed = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;

  return new Date(
    Date.UTC(
      parsed.getUTCFullYear(),
      parsed.getUTCMonth(),
      parsed.getUTCDate(),
      endOfDay ? 23 : 0,
      endOfDay ? 59 : 0,
      endOfDay ? 59 : 0,
      endOfDay ? 999 : 0
    )
  );
};

const getNormalizedRange = (
  range: { start?: string; end?: string } | [string?, string?] | null
) => {
  if (!range) return { start: null as Date | null, end: null as Date | null };
  const startRaw = Array.isArray(range) ? range[0] : range.start;
  const endRaw = Array.isArray(range) ? range[1] : range.end;

  return {
    start: buildDateBoundary(startRaw as any, false),
    end: buildDateBoundary(endRaw as any, true),
  };
};

const STATUS_FORM_VALUE_MAP: Record<1 | 2 | 3, "PAID" | "UNPAID" | "CANCELLED"> = {
  1: "PAID",
  2: "UNPAID",
  3: "CANCELLED",
};

const resolveStatusFormValue = (
  status: unknown,
  fallbackId?: unknown
):
  | "PAID"
  | "UNPAID"
  | "CANCELLED"
  | "PARTIALLY_PAID"
  | "REFUNDED"
  | "PARTIALLY_REFUNDED"
  | "PENDING" => {
  const allowed = new Set([
    "PAID",
    "UNPAID",
    "CANCELLED",
    "PARTIALLY_PAID",
    "REFUNDED",
    "PARTIALLY_REFUNDED",
    "PENDING",
  ]);

  if (typeof status === "string" && status.trim()) {
    const normalized = status.trim().toUpperCase();
    // Normalize VOID/VOIDED to CANCELLED
    if (normalized === "VOID" || normalized === "VOIDED") {
      return "CANCELLED";
    }
    if (allowed.has(normalized)) {
      return normalized as
        | "PAID"
        | "UNPAID"
        | "CANCELLED"
        | "PARTIALLY_PAID"
        | "REFUNDED"
        | "PARTIALLY_REFUNDED"
        | "PENDING";
    }
  }

  if (typeof fallbackId === "string" && fallbackId.trim()) {
    const normalized = fallbackId.trim().toUpperCase();
    // Normalize VOID/VOIDED to CANCELLED
    if (normalized === "VOID" || normalized === "VOIDED") {
      return "CANCELLED";
    }
    if (allowed.has(normalized)) {
      return normalized as
        | "PAID"
        | "UNPAID"
        | "CANCELLED"
        | "PARTIALLY_PAID"
        | "REFUNDED"
        | "PARTIALLY_REFUNDED"
        | "PENDING";
    }
  }

  const numeric = Number(status ?? fallbackId);
  if (Number.isFinite(numeric)) {
    const formValue = STATUS_FORM_VALUE_MAP[numeric as 1 | 2 | 3];
    if (formValue) {
      return formValue;
    }
  }

  return "PENDING";
};

  const resolveInvoiceStatus = (invoice: any, notes: any[]) => {
    const normalizedStatus =
      typeof invoice?.status === "string"
        ? invoice.status.toUpperCase()
        : undefined;

    if (normalizedStatus === "CANCELLED") return "CANCELLED";
    if (normalizedStatus === "PAID") return "PAID";
    if (normalizedStatus === "PARTIALLY_PAID") return "PARTIALLY_PAID";

    const creditNotes = (Array.isArray(notes) ? notes : []).filter(
      (n) => n?.type === "CREDIT_NOTE"
    );

    const totalCredit = creditNotes.reduce(
      (sum, n) => sum + Number(n?.amount || 0),
      0
    );

    const totalAmount = Number(
      invoice?.total ??
        invoice?.amount ??
        invoice?.total_amount ??
        invoice?.totalAmount ??
        invoice?.subtotal ??
        0
    );

    const balance = Number(
      invoice?.balance ??
        invoice?.remaining ??
        invoice?.remaining_amount ??
        invoice?.remainingAmount ??
        0
    );

    const paidAmount = Number(
      invoice?.paid_amount ??
        invoice?.paidAmount ??
        (Number.isFinite(totalAmount) && Number.isFinite(balance)
          ? totalAmount - balance
          : 0)
    );

    // If everything is zero, treat as cancelled to match UX requirement.
    if (
      [totalAmount, balance, paidAmount].every(
        (v) => Number.isFinite(v) && Math.abs(v) === 0
      )
    ) {
      return "CANCELLED";
    }

    // If credit fully offsets the invoice, mark as cancelled.
    if (
      Number.isFinite(totalAmount) &&
      totalAmount > 0 &&
      totalCredit >= totalAmount
    ) {
      return "CANCELLED";
    }

    return "PENDING";
  };

  React.useEffect(() => {
    const fetchBranches = async () => {
      try {
        if (!venueId) {
          setBranches([]);
          return;
        }

        const response = await getAllBranches(venueId);

        let branchesData = [];
        if (response && response.units) {
          branchesData = response.units;
        } else if (response && response.data) {
          branchesData = response.data;
        } else if (Array.isArray(response)) {
          branchesData = response;
        }

        console.log("Processed branches data:", branchesData);
        setBranches(Array.isArray(branchesData) ? branchesData : []);
      } catch (error) {
        console.error("Error fetching branches:", error);
        setBranches([]);
      }
    };

    if (venueId) {
      fetchBranches();
    } else {
      console.log("No venueId available, skipping branch fetch");
    }
  }, [venueId]);

  // One page of invoices: search, status, branch and dates are all filtered
  // by the server, which also sums the amounts over every match.
  const loadInvoicesPage = React.useCallback(
    async ({
      page,
      size,
      search: searchTerm,
      status,
      withSummary,
    }: {
      page: number;
      size: number;
      search?: string;
      status?: string;
      withSummary?: boolean;
    }): Promise<{
      rows: any[];
      totalElements: number;
      totalPages: number;
      summary: InvoicesSummary | null;
    }> => {
    const empty = { rows: [], totalElements: 0, totalPages: 1, summary: null };
    const isAllSelection = selectValue === "all";
    const isBusinessOnly = selectValue === BUSINESS_SCOPE_VALUE;
    const isBusinessScope = isAllSelection || isBusinessOnly;
    const businessId = isBusinessScope ? venueId : selectValue;

    if (isBusinessScope && !venueId) {
      console.warn("No venueId available, skipping admin invoices fetch");
      return empty;
    }

    if (!businessId && !isBusinessScope) {
      return empty;
    }

    {
      const params: Record<string, string> = {
        page: String(page),
        size: String(size),
      };
      const trimmedSearch = String(searchTerm || "").trim();
      if (trimmedSearch) params.search = trimmedSearch;
      if (status && status !== "all") params.status = String(status).toUpperCase();
      if (isBusinessOnly) params.businessLevelOnly = "true";
      if (withSummary) params.summary = "true";

      const { start: rangeStart, end: rangeEnd } = getNormalizedRange(dateRange);
      if (rangeStart) {
        params.from = rangeStart.toISOString().split("T")[0]; // YYYY-MM-DD
      }
      if (rangeEnd) {
        params.to = rangeEnd.toISOString().split("T")[0]; // YYYY-MM-DD
      }

      const rawResponse: any = isBusinessScope
        ? await getInvoicesByBusiness(String(venueId), params)
        : await getInvoicesByUnit(String(selectValue), params);
      const apiResponse = rawResponse?.data || rawResponse;
      const invoicesData =
        apiResponse?.invoices ||
        apiResponse?.content ||
        apiResponse?.data?.invoices ||
        apiResponse?.data?.content ||
        [];
      const responseUnitName = apiResponse?.unit?.name || "";
      const responseUnitId = apiResponse?.unit?.id || "";
      const responseBusinessName = apiResponse?.business?.name || "";
      const totalElementsCount = Number(apiResponse?.totalElements);
      const totalPagesCount = Number(apiResponse?.totalPages);

      // SIMPLIFIED DATA MAPPING (50% less transformation)
      // Backend should return pre-formatted data matching this structure:
      const normalizedInvoices = (Array.isArray(invoicesData) ? invoicesData : []).map(
        (item: any) => {
          const amount = Number(item.amount ?? item.total ?? item.total_amount ?? 0) || 0;
          const balance = Number(item.balance ?? item.remaining_amount ?? item.remaining ?? 0) || 0;
          const appointmentStartAt =
            item?.appointment?.start_at ||
            item?.appointment?.startAt ||
            item?.appointmentStartAt ||
            item?.appointment_start_at ||
            item?.appointment?.start_at_utc ||
            item?.appointment?.startAtUtc ||
            "";
          const appointmentDateValue =
            appointmentStartAt ||
            item?.dueAt ||
            item?.due_at ||
            item?.invoice?.dueAt ||
            item?.invoice?.due_at ||
            item?.issuedAt ||
            item?.createdAt ||
            item?.created_at ||
            "";
          // `services`/`items` can legitimately come back as an empty array (class
          // and package invoices only carry raw billing items, no resolved
          // `services`), so an empty array must fall through instead of winning
          // the `||` chain just for being truthy.
          const itemMetadata =
            item?.metadata && typeof item.metadata === "object"
              ? item.metadata
              : {};
          const serviceList =
            (Array.isArray(item.services) && item.services.length
              ? item.services
              : null) ||
            (item.service ? [item.service] : null) ||
            (itemMetadata.service ? [itemMetadata.service] : null) ||
            (Array.isArray(item.items) && item.items.length ? item.items : null) ||
            [];
          const serviceNames = (Array.isArray(serviceList) ? serviceList : [])
            .map((svc: any) => svc?.name || svc?.description || "")
            .filter(Boolean)
            .join(", ");
          const productEntries = toProductEntries(item);
          const businessName = pickNonEmptyString(
            item.businessName,
            item.business?.name,
            item.unit?.business?.name,
            item.metadata?.businessName,
            responseBusinessName
          );
          const unitName = pickNonEmptyString(
            item.unitName,
            item.unit?.name,
            responseUnitName
          );
          // An invoice raised on the business itself has no branch, which is
          // exactly the case the label below renders as "<business> (Business)".
          const isBusinessLevel = !unitName;
          const branchLabel =
            unitName || (businessName ? `${businessName} (Business)` : "N/A");

          return {
            id: item.invoiceId || item.id,
            Invoice_number: item.invoiceId || item.id,
            client_id: resolveInvoiceClientId(item),
            client_name: resolveInvoiceClientName(item),
            mobile: resolveInvoiceClientPhone(item),
            email: resolveInvoiceClientEmail(item),
            invoice_date: appointmentDateValue ? String(appointmentDateValue) : "",
            branch: branchLabel,
            // Derived from the same lookup that builds the label, so the
            // business filter always keeps exactly the rows shown as
            // "<business> (Business)" rather than relying on a unit id field
            // whose name varies across the invoice payloads.
            is_business_level: isBusinessLevel,
            type: item.type || "INVOICE",
            status: item.status || "PENDING",
            rawStatus: item.status || "PENDING",
            method: String(item.paymentMethod || item.method || item.payment_method || "cash"),
            items: Array.isArray(serviceList) ? serviceList : [],
            services: serviceNames || "N/A",
            productEntries,
            products: productEntries.join(", ") || "N/A",
            net_amount: amount,
            total_amount: amount,
            paid_amount: amount - balance,
            remaining_amount: balance,
            notes: item.notes || "N/A",
            status_id: item.status || "",
            payment_method_id: item.paymentMethodId || "",
            created_at: item.issuedAt || item.createdAt || "",
            paid_at: resolveInvoicePaidAt(item),
            dueAt: item.dueAt || "",
            metadata: item.metadata || {},
            client: item.client,
            business_id:
              item.businessId ||
              item.business?.id ||
              item.unit?.business?.id ||
              item.metadata?.businessId ||
              responseUnitId ||
              "",
          };
        }
      );

      return {
        rows: normalizedInvoices,
        totalElements: Number.isFinite(totalElementsCount)
          ? totalElementsCount
          : normalizedInvoices.length,
        totalPages: Number.isFinite(totalPagesCount) && totalPagesCount > 0
          ? totalPagesCount
          : 1,
        summary: apiResponse?.summary ?? null,
      };
    }
    },
    [dateRange, selectValue, venueId]
  );

  const [invoicesRefreshKey, setInvoicesRefreshKey] = React.useState(0);
  const [invoicesSummary, setInvoicesSummary] =
    React.useState<InvoicesSummary | null>(null);
  // Actions call this after a change; the table then reloads its page.
  const fetchInvoices = React.useCallback(async () => {
    setInvoicesRefreshKey((key) => key + 1);
  }, []);
  const debouncedSearch = useDebouncedValue(search, 400);

  const fetchInvoicesTablePage = React.useCallback(
    async (params?: any) => {
      const page = Number(params?.page) || 0;
      const size = Number(params?.size) || INVOICES_PAGE_SIZE;
      setInvoicesLoading(true);
      try {
        const result = await loadInvoicesPage({
          page,
          size,
          search: String(params?.search || ""),
          status: statusFilter,
          withSummary: true,
        });
        // Row actions (pay, cancel, edit) look invoices up on this page.
        setInvoices(result.rows);
        setInvoicesSummary(result.summary);
        return {
          content: result.rows,
          number: page,
          size,
          totalElements: result.totalElements,
          totalPages: result.totalPages,
        };
      } catch (error) {
        console.error("Error fetching invoices:", error);
        setInvoices([]);
        setInvoicesSummary(null);
        throw error;
      } finally {
        setInvoicesLoading(false);
      }
    },
    [loadInvoicesPage, statusFilter]
  );

  interface GetAllServicesResponse {
    data?: {
      services?: any[];
    };
    services?: any[];
  }
  React.useEffect(() => {
    const fetchServices = async () => {
      const selectedScope = addOpen
        ? String(invoiceFormUnitId || "all")
        : toUnitId(selectValue) || "all";
      const serviceContextId =
        selectedScope === "all" ? String(venueId || "") : selectedScope;

      if (!serviceContextId) {
        setServices([]);
        return;
      }
      try {
        const response = (await getAllServices(
          serviceContextId
        )) as GetAllServicesResponse;
        console.log("Services API response:", response);
        const servicesData =
          response?.data?.services ||
          response?.services ||
          (Array.isArray(response) ? response : []);
        setServices(Array.isArray(servicesData) ? servicesData : []);
      } catch (error) {
        console.error("Error fetching services:", error);
        setServices([]);
      }
    };
    fetchServices();
  }, [addOpen, invoiceFormUnitId, selectValue, venueId]);

  // `clients` is a cache: the first clients of the business, plus picker
  // search results and the clients on the invoice being edited.
  const loadInvoiceClients = React.useCallback(
    async ({ search, ids }: { search?: string; ids?: string[] } = {}) => {
      if (!venueId) return;
      try {
        const response = await getClientsSelect({
          businessId: String(venueId),
          ids: ids && ids.length > 0 ? ids : undefined,
          search,
          limit: search !== undefined ? 30 : ids?.length ? 1 : 50,
        });
        const clientsData = Array.isArray(response?.content) ? response.content : [];
        const mappedClients: InvoiceClientOption[] = clientsData
          .map((c: any) => {
            const id = c?.id;
            if (!id) return null;
            return {
              id: String(id),
              name: String(c?.name || c?.label || c?.email || `Client ${id}`),
              email: c?.email || "",
              mobile: c?.mobile || c?.phone || "",
            } as InvoiceClientOption;
          })
          .filter(Boolean) as any[];
        setClients((prev) => {
          const byId = new Map(prev.map((c) => [String(c.id), c]));
          mappedClients.forEach((c) => byId.set(String(c.id), c));
          return Array.from(byId.values());
        });
      } catch (error) {
        console.error("Error fetching clients:", error);
      }
    },
    [venueId]
  );

  React.useEffect(() => {
    setClients([]);
    loadInvoiceClients();
  }, [loadInvoiceClients]);

  const clientSearchTimerRef = React.useRef<number | undefined>(undefined);
  const handleClientSearchInput = React.useCallback(
    (term: string) => {
      window.clearTimeout(clientSearchTimerRef.current);
      const trimmed = term.trim();
      if (trimmed.length < 2) return;
      clientSearchTimerRef.current = window.setTimeout(() => {
        void loadInvoiceClients({ search: trimmed });
      }, 300);
    },
    [loadInvoiceClients]
  );
  React.useEffect(() => () => window.clearTimeout(clientSearchTimerRef.current), []);

  // The invoice being edited can name a payer or guests outside the cache.
  const editingClientIdsKey = React.useMemo(() => {
    if (!editingRow) return "";
    const metadata = editingRow?.metadata || editingRow?.invoice?.metadata || {};
    const ids = [
      editingRow?.client_id,
      editingRow?.invoice?.client_id,
      editingRow?.clientOrgUserId,
      metadata?.clientOrgUserId,
      ...(Array.isArray(metadata?.sharedClientIds) ? metadata.sharedClientIds : []),
      ...(Array.isArray(metadata?.shared_client_ids) ? metadata.shared_client_ids : []),
    ]
      .map((id) => String(id || "").trim())
      .filter(Boolean);
    return Array.from(new Set(ids)).sort().join(",");
  }, [editingRow]);
  React.useEffect(() => {
    if (!editingClientIdsKey) return;
    const known = new Set(clients.map((c) => String(c.id)));
    const missing = editingClientIdsKey.split(",").filter((id) => !known.has(id));
    if (missing.length > 0) void loadInvoiceClients({ ids: missing });
    // Only when the invoice changes, not on every cache update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingClientIdsKey, loadInvoiceClients]);

  React.useEffect(() => {
    let mounted = true;

    const loadRewardData = async () => {
      try {
        setLoadingLoyaltyRewards(true);
        const [rewardsResponse, productsResponse, stockResponse, servicesResponse] =
          await Promise.all([
            getRewards().catch(() => []),
            fetchProducts().catch(() => []),
            fetchStockSummary().catch(() => null),
            venueId ? getAllServicesByBusiness(String(venueId)).catch(() => []) : [],
          ]);

        if (!mounted) return;

        const rewards = Array.isArray(rewardsResponse)
          ? rewardsResponse
          : Array.isArray((rewardsResponse as any)?.rewards)
            ? (rewardsResponse as any).rewards
            : Array.isArray((rewardsResponse as any)?.data)
              ? (rewardsResponse as any).data
              : [];
        setLoyaltyRewards(
          rewards.filter((reward: any) => reward?.active !== false),
        );

        const productList = Array.isArray(productsResponse)
          ? productsResponse
          : Array.isArray((productsResponse as any)?.data)
            ? (productsResponse as any).data
            : Array.isArray((productsResponse as any)?.products)
              ? (productsResponse as any).products
              : [];
        setProducts(productList);

        const stockList = Array.isArray((stockResponse as any)?.items)
          ? (stockResponse as any).items
          : Array.isArray((stockResponse as any)?.data)
            ? (stockResponse as any).data
            : Array.isArray(stockResponse)
              ? stockResponse
              : [];
        setStockItems(stockList);

        const serviceList = Array.isArray((servicesResponse as any)?.data?.services)
          ? (servicesResponse as any).data.services
          : Array.isArray((servicesResponse as any)?.services)
            ? (servicesResponse as any).services
            : Array.isArray(servicesResponse)
              ? servicesResponse
              : [];
        setRewardServices(serviceList);
      } finally {
        if (mounted) setLoadingLoyaltyRewards(false);
      }
    };

    loadRewardData();

    return () => {
      mounted = false;
    };
  }, [venueId]);

  const isEditingInvoice = Boolean(editOpen && editingRow);

  const ALL_INVOICE_STATUSES = [
    "PENDING",
    "PAID",
    "PARTIALLY_PAID",
    "UNPAID",
    "REFUNDED",
    "PARTIALLY_REFUNDED",
    "CANCELLED",
  ];

  const ALLOWED_STATUS_TRANSITIONS = new Set<string>([
    "PENDING->PENDING",
    "PENDING->PAID",
    "PENDING->CANCELLED",
    "UNPAID->UNPAID",
    "UNPAID->PAID",
    "UNPAID->CANCELLED",
    "PARTIALLY_PAID->PARTIALLY_PAID",
    "PARTIALLY_PAID->PAID",
    "PARTIALLY_PAID->UNPAID",
    "PARTIALLY_PAID->CANCELLED",
    "PAID->PAID",
    "PAID->UNPAID",
    "PAID->REFUNDED",
    "PAID->CANCELLED",
    "REFUNDED->REFUNDED",
    "REFUNDED->PAID",
    "REFUNDED->CANCELLED",
    "PARTIALLY_REFUNDED->PARTIALLY_REFUNDED",
    "PARTIALLY_REFUNDED->PAID",
    "PARTIALLY_REFUNDED->UNPAID",
    "PARTIALLY_REFUNDED->REFUNDED",
    "PARTIALLY_REFUNDED->CANCELLED",
    "CANCELLED->CANCELLED",
    "CANCELLED->PAID",
  ]);

  const normalizeInvoiceStatus = (status: any) => {
    const normalized = typeof status === "string" ? status.trim().toUpperCase() : "";
    // Treat VOID/VOIDED as CANCELLED
    if (normalized === "VOID" || normalized === "VOIDED") {
      return "CANCELLED";
    }
    return normalized;
  };

  const currentInvoiceStatus = normalizeInvoiceStatus(
    isEditingInvoice
      ? editingRow?.invoice?.status ||
          editingRow?.status ||
          editingRow?.invoice?.status_name ||
          editingRow?.status_name ||
          "PENDING"
      : "PENDING"
  );

  const filteredStatusOptions =
    isEditingInvoice && currentInvoiceStatus
      ? ALL_INVOICE_STATUSES.filter((status) =>
          ALLOWED_STATUS_TRANSITIONS.has(
            `${currentInvoiceStatus}->${normalizeInvoiceStatus(status)}`
          )
        )
      : ALL_INVOICE_STATUSES;

  const statusOptions =
    filteredStatusOptions.length > 0 ? filteredStatusOptions : ALL_INVOICE_STATUSES;

  // Limit edit modal to Pay / Unpay; cancel handled via dedicated dialog; keep current status for context
  let limitedStatusOptions = statusOptions.filter((status) => {
    const normalized = normalizeInvoiceStatus(status);
    return (
      normalized === currentInvoiceStatus ||
      normalized === "PAID" ||
      normalized === "UNPAID" ||
      normalized === "CANCELLED"
    );
  });

  // Pending can only stay pending or go to paid here
  if (currentInvoiceStatus === "PENDING") {
    limitedStatusOptions = limitedStatusOptions.filter((status) => {
      const normalized = normalizeInvoiceStatus(status);
      return normalized === "PENDING" || normalized === "PAID";
    });
  }

  // Cancelled (including VOID) is read-only in this modal
  if (currentInvoiceStatus === "CANCELLED") {
    limitedStatusOptions = limitedStatusOptions.filter(
      (status) => normalizeInvoiceStatus(status) === "CANCELLED"
    );
  }

  // Fallback: if options empty, show current + allowed transitions
  if (limitedStatusOptions.length === 0) {
    limitedStatusOptions = [currentInvoiceStatus, "PAID", "UNPAID", "CANCELLED"].filter(
      (v, i, a) => a.indexOf(v) === i
    );
  }
  const rewardEditingLocked = Boolean(
    editingRow && hasInvoiceSuccessfulPayment(editingRow)
  );

  const formFields: Field[] = [
    {
      name: "clientName",
      label: "Client Name",
      colSpan: 1,
      customRender: (value: string, onChange: (v: string) => void, helpers) => {
        const invoiceClients = invoices
          .map((inv: any) =>
            inv.client_id && inv.client_name
              ? {
                  id: inv.client_id,
                  name:
                    inv.client_name ||
                    inv.client?.name ||
                    inv.client?.full_name ||
                    inv.client?.fullName,
                  email: inv.client?.email || inv.email,
                  mobile: inv.client?.mobile || inv.mobile,
                }
              : null
          )
          .filter(Boolean) as any[];

        const mergedClients = [
          ...(Array.isArray(clients) ? clients : []),
          ...invoiceClients,
        ].reduce((acc, c: any) => {
          const id =
            c?.id ??
            c?.value ??
            c?.client_id ??
            c?._id ??
            c?.user_id ??
            c?.clientId;
          if (!id) return acc;
          const key = String(id);
          if (!acc.has(key)) {
            acc.set(key, {
              id: key,
              name:
                c?.name ||
                c?.label ||
                c?.client_name ||
                c?.fullName ||
                c?.full_name ||
                c?.email ||
                `Client ${key}`,
              email: c?.email || "",
              mobile:
                c?.mobile ||
                c?.phone ||
                c?.phoneNumber ||
                c?.mobileNumber ||
                "",
            });
          }
          return acc;
        }, new Map<string, InvoiceClientOption>());

        const clientList: InvoiceClientOption[] = Array.from(
          mergedClients.values()
        );

        const options = clientList.map((client: any) => ({
          label: client.name,
          value: client.id,
        }));
        return (
          <>
            <CustomLabel
              sx={{ p: 0.1 }}
              text={"Client Name"}
              color={COLORS.typographyColor}
              fontWeight={500}
              fontSize={14}
            />
            <CustomSelect
              label="Client Name"
              value={value || ""}
              options={options}
              searchable
              onSearchInput={handleClientSearchInput}
              disabled={isEditingInvoice}
              onChange={(v: any) => {
                onChange(String(v || ""));
                if (v) {
                  const selected = clientList.find(
                    (c) => String(c.id) === String(v)
                  );
                  if (selected) {
                    helpers?.setFieldValue("email", selected.email || "");
                    helpers?.setFieldValue(
                      "phoneNumber",
                      selected.mobile || ""
                    );
                    return;
                  }
                  getClientById(String(v))
                    .then((client) => {
                      if (client && client.user) {
                        helpers?.setFieldValue(
                          "email",
                          client.user.email || ""
                        );
                        helpers?.setFieldValue(
                          "phoneNumber",
                          client.user.mobile || ""
                        );
                      }
                    })
                    .catch(console.error);
                }
              }}
              fullWidth
              placeholder="Select..."
            />
          </>
        );
      },
    },
    { name: "email", label: "Email", disabled: true },
    { name: "phoneNumber", label: "Phone Number", disabled: true },
    { name: "invoice_date", label: "Date", type: "date" },
    {
      name: "status",
      label: "Status",
      type: "dropdown",
      options: limitedStatusOptions,
    },
    {
      name: "paymentMethod",
      label: "Payment method",
      type: "dropdown",
      options: ["cash"],
      disabled: true,
    },
    {
      name: "loyaltyRedeemPoints",
      label: "Loyalty",
      colSpan: 2,
      customRender: (value: any, _onChange: (v: any) => void, helpers) => (
        <LoyaltyInvoicePreview
          value={value}
          values={{
            ...(helpers?.values || {}),
            sourceInvoice: editingRow,
          }}
          clients={clients as any[]}
          onDiscountChange={(discount) =>
            helpers?.setFieldValue("discount", discount)
          }
        />
      ),
    },
    {
      name: "loyaltyRewardId",
      label: "Reward Package",
      colSpan: 2,
      customRender: (value: string, onChange: (v: string) => void, helpers) => (
        <InvoiceRewardPackageField
          value={value}
          onChange={onChange}
          helpers={helpers}
          invoiceEditingRow={editingRow}
          loyaltyRewards={loyaltyRewards}
          products={products}
          services={services}
          rewardServices={rewardServices}
          stockItems={stockItems}
          venueId={venueId}
          loadingLoyaltyRewards={loadingLoyaltyRewards}
          rewardEditingLocked={rewardEditingLocked}
        />
      ),
    },
    { name: "notes", label: "Notes", colSpan: 2 },
    {
      name: "items",
      label: "Items",
      colSpan: 2,
      customRender: (_value: any, onChange: (v: any) => void, helpers) => (
        <InvoiceItemsEditor
          value={_value}
          onChange={onChange}
          vatValue={helpers?.values?.vat || 0}
          onVatChange={(v) => helpers?.setFieldValue("vat", v)}
          discountValue={helpers?.values?.discount || 0}
          onDiscountChange={(v) => helpers?.setFieldValue("discount", v)}
          promoDiscountValue={helpers?.values?.promoDiscount || 0}
          onPromoDiscountChange={(v) =>
            helpers?.setFieldValue("promoDiscount", v)
          }
          promoValue={helpers?.values?.promoValue || 0}
          promoType={helpers?.values?.promoType || ""}
          onPromoValueChange={(v) => helpers?.setFieldValue("promoValue", v)}
          isEditMode={editOpen}
          servicesList={services}
        />
      ),
    },
  ];

  const addInvoiceFormFields: Field[] = [
    {
      name: "unitId",
      label: "Business / Unit",
      colSpan: 1,
      customRender: (value: string, onChange: (v: string) => void) => (
        <>
          <CustomLabel
            sx={{ p: 0.1 }}
            text={"Business / Unit"}
            color={COLORS.typographyColor}
            fontWeight={500}
            fontSize={14}
          />
          <CustomSelect
            label="Business / Unit"
            value={value || "all"}
            options={[
              { label: `All (${businessDisplayName})`, value: "all" },
              ...branches.map((branch) => ({
                label: branch.name || "Unknown Branch",
                value: String(branch.id || branch._id || ""),
              })),
            ]}
            onChange={(v: any) => {
              const nextValue = String(v || "all");
              onChange(nextValue);
              setInvoiceFormUnitId(nextValue);
            }}
            fullWidth
            searchable
            placeholder="Select business or branch"
          />
        </>
      ),
    },
    ...formFields.filter(
      (field) =>
        field.name !== "loyaltyRedeemPoints" &&
        field.name !== "loyaltyRewardId"
    ),
  ];

  const openEditForId = async (id: string | number) => {
    try {
      const response = await getInvoiceById(String(id)) as any;
      const invoiceData = response?.data || response;

      const unitId =
        invoiceData?.unit?.id ||
        invoiceData?.invoice?.unitId ||
        invoiceData?.invoice?.unit_id ||
        invoiceData?.unitId ||
        invoiceData?.unit_id;

      const businessId =
        invoiceData?.business?.id ||
        invoiceData?.invoice?.business_id ||
        invoiceData?.business_id ||
        invoiceData?.businessId;

      if (unitId) {
        try {
          const servicesResponse = await getAllServices(String(unitId)) as any;
          const servicesData =
            servicesResponse?.data?.services ||
            servicesResponse?.services ||
            (Array.isArray(servicesResponse) ? servicesResponse : []);
          setServices(Array.isArray(servicesData) ? servicesData : []);
          console.log("Services fetched for invoice edit:", servicesData);
        } catch (error) {
          console.error("Error fetching services for unit:", error);
          setServices([]);
        }
      } else if (businessId) {
        // fallback: fetch by business if unit missing
        try {
          const servicesResponse = await getAllServices(String(businessId)) as any;
          const servicesData =
            servicesResponse?.data?.services ||
            servicesResponse?.services ||
            (Array.isArray(servicesResponse) ? servicesResponse : []);
          setServices(Array.isArray(servicesData) ? servicesData : []);
        } catch (error) {
          setServices([]);
        }
      } else {
        setServices([]);
      }

      setEditingRow(invoiceData);
      setEditOpen(true);
    } catch (error) {
      console.error("Error fetching invoice:", error);
    }
  };

  const wiredActions = Actions({
    handleOpenEditModal:{handler: openEditForId},
    onPay: {
      handler: async (id) => {
        setToPayId(id);
        setPaymentConfirmOpen(true);
      },
      visible: (row: any) =>
        row.type === "INVOICE" &&
        row.status !== "PAID" &&
        row.status !== "CANCELLED",
    },

    onCancel: {
      handler: async (id) => {
        setToCancelId(id);
        setCancelReason("");
        setCancelConfirmOpen(true);
      },
      visible: (row: any) =>
        row.type === "INVOICE" && row.status !== "CANCELLED",
    },

    onView: {
      handler: async (id) => {
        try {
          const response = await getInvoiceById(String(id));
          const invoiceData = (response as any)?.data || response;
          setSelectedInvoice(invoiceData);
          setViewDialogOpen(true);
        } catch (error) {
          console.error("Error fetching invoice details:", error);
        }
      },
      visible: (row: any) => row.type === "INVOICE",
    },

    onPdf: {
      handler: async (id) => {
        const businessId = params.businessId || params.id;
        navigate(`/dashboard/${businessId}/invoicepdf/${id}`);
      },
    },

    onApprove: {
      handler: async (id) => {
        try {
          await approveQuotation(Number(id));
          toast.success("Quotation approved successfully");
          fetchInvoices();
        } catch (error) {
          showErrorToast(
            getErrorMessage(error, "Failed to approve quotation")
          );
        }
      },
      visible: (row: any) => row.type === "QUOTATION",
    },

    onReject: {
      handler: async (id) => {
        try {
          await rejectQuotation(Number(id));
          toast.success("Quotation rejected");
          fetchInvoices();
        } catch (error) {
          showErrorToast(getErrorMessage(error, "Failed to reject quotation"));
        }
      },
      visible: (row: any) => row.type === "QUOTATION",
    },
  });

  const [rows, setRows] = React.useState(() => {
    const total = 10;
    return Array.from({ length: total }, (_, i) => {
      const id = i + 1;
      const start = new Date();
      start.setDate(start.getDate() + i);
      const end = new Date(start.getTime() + 60 * 60000);
      return {
        id,
        bookingSource: id % 2 === 0 ? "Website" : "App",
        createdBy: `User ${((id - 1) % 5) + 1}`,
        phoneNumber: `+1-202-555-${String(1000 + id).slice(-4)}`,
        priority: id % 3 === 0 ? "High" : "Normal",
        status: id % 3 === 0 ? "paid" : id % 3 === 1 ? "unpaid" : "Cancelled",
        date: start.toISOString(),
        branch: `Branch ${((id - 1) % 3) + 1}`,
        startTime: start.toTimeString().slice(0, 5),
        endTime: end.toTimeString().slice(0, 5),
      };
    });
  });

  React.useEffect(() => {
    const b = Array.from(new Set(rows.map((r: any) => r.branch)));
    setBranchList(b.sort());
  }, [rows]);

  React.useEffect(() => {
    if (branches.length > 0 && !selectValue) {
      setSelectValue("all");
    }
  }, [branches, selectValue]);

  const [invoiceItems, setInvoiceItems] = React.useState<InvoiceItem[]>([]);

  const [tableDialogOpen, setTableDialogOpen] = React.useState(false);

  const exportXlsx = React.useCallback(async (reportProgress?: ExportProgressReporter) => {
    // The table holds one page; the file gets every invoice in scope.
    const invoices: any[] = [];
    try {
      for (let page = 0; page < 1000; page += 1) {
        const result = await loadInvoicesPage({ page, size: 200 });
        invoices.push(...result.rows);
        reportProgress?.(page + 1, result.totalPages);
        if (page + 1 >= result.totalPages || result.rows.length === 0) break;
      }
    } catch (error) {
      console.error("Error exporting invoices:", error);
      toast.error("Failed to export invoices.");
      return;
    }
    if (invoices.length === 0) return;

    const columns = [
      { key: "Invoice_number", header: "Invoice Number" },
      { key: "client_name", header: "Client Name" },
      { key: "mobile", header: "Mobile" },
      { key: "invoice_date", header: "Invoice Date" },
      { key: "created_at", header: "Created At" },
      { key: "paid_at", header: "Paid At" },
      { key: "branch", header: "Branch" },
      { key: "type", header: "Type" },
      { key: "status", header: "Status" },
      { key: "services", header: "Services" },
      { key: "products", header: "Products" },
      { key: "net_amount", header: "Net Amount" },
      { key: "paid_amount", header: "Paid Amount" },
      { key: "remaining_amount", header: "Remaining Amount" },
    ];

    const formatDateOnly = (value: any) => {
      if (!value) return "-";
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? String(value) : formatTableDate(d);
    };

    const sheetRows: string[][] = [
      columns.map((column) => column.header),
      ...invoices.map((r: any) => {
      const row: Record<string, any> = {
        Invoice_number: r.Invoice_number ?? "-",
        client_name: r.client_name ?? "-",
        mobile: r.mobile ?? "-",
        invoice_date: formatDateOnly(r.invoice_date),
        created_at: formatDateOnly(r.created_at),
        paid_at: formatDateOnly(r.paid_at),
        branch: r.branch ?? "-",
        type: r.type ?? "-",
        status: r.status ?? r.rawStatus ?? "-",
        services: r.services ?? "-",
        // Export gets the full list, not the truncated cell label.
        products: r.products ?? "-",
        net_amount: r.net_amount ?? "-",
        paid_amount: r.paid_amount ?? "-",
        remaining_amount: r.remaining_amount ?? "-",
      };

        return columns.map((column) => String(row[column.key] ?? "-"));
      }),
    ];

    const escapeXml = (value: any) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    const columnName = (index: number) => {
      let name = "";
      let current = index + 1;
      while (current > 0) {
        const remainder = (current - 1) % 26;
        name = String.fromCharCode(65 + remainder) + name;
        current = Math.floor((current - 1) / 26);
      }
      return name;
    };
    const worksheetRows = sheetRows
      .map((row, rowIndex) => {
        const cells = row
          .map((cell, colIndex) => {
            const style = rowIndex === 0 ? ' s="1"' : "";
            return `<c r="${columnName(colIndex)}${rowIndex + 1}" t="inlineStr"${style}><is><t>${escapeXml(
              cell
            )}</t></is></c>`;
          })
          .join("");
        return `<row r="${rowIndex + 1}">${cells}</row>`;
      })
      .join("");
    const worksheetXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><cols>${columns
      .map((_, idx) => `<col min="${idx + 1}" max="${idx + 1}" width="18" customWidth="1"/>`)
      .join("")}</cols><sheetData>${worksheetRows}</sheetData></worksheet>`;
    const workbookXml =
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="Invoices" sheetId="1" r:id="rId1"/></sheets></workbook>';
    const workbookRelsXml =
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>';
    const relsXml =
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>';
    const stylesXml =
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts><fills count="1"><fill><patternFill patternType="none"/></fill></fills><borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs></styleSheet>';
    const contentTypesXml =
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>';

    const encoder = new TextEncoder();
    const crcTable = Array.from({ length: 256 }, (_, n) => {
      let c = n;
      for (let k = 0; k < 8; k += 1) {
        c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      }
      return c >>> 0;
    });
    const crc32 = (data: Uint8Array) => {
      let crc = 0xffffffff;
      data.forEach((byte) => {
        crc = crcTable[(crc ^ byte) & 0xff] ^ (crc >>> 8);
      });
      return (crc ^ 0xffffffff) >>> 0;
    };
    const u16 = (value: number): number[] => [
      value & 0xff,
      (value >>> 8) & 0xff,
    ];
    const u32 = (value: number): number[] => [
      value & 0xff,
      (value >>> 8) & 0xff,
      (value >>> 16) & 0xff,
      (value >>> 24) & 0xff,
    ];
    const files = [
      { name: "[Content_Types].xml", content: contentTypesXml },
      { name: "_rels/.rels", content: relsXml },
      { name: "xl/workbook.xml", content: workbookXml },
      { name: "xl/_rels/workbook.xml.rels", content: workbookRelsXml },
      { name: "xl/styles.xml", content: stylesXml },
      { name: "xl/worksheets/sheet1.xml", content: worksheetXml },
    ];
    const localParts: Uint8Array[] = [];
    const centralParts: Uint8Array[] = [];
    let offset = 0;

    files.forEach((file) => {
      const nameBytes = encoder.encode(file.name);
      const data = encoder.encode(file.content);
      const crc = crc32(data);
      const localHeader: Uint8Array = new Uint8Array([
        ...u32(0x04034b50),
        ...u16(20),
        ...u16(0),
        ...u16(0),
        ...u16(0),
        ...u16(0),
        ...u32(crc),
        ...u32(data.length),
        ...u32(data.length),
        ...u16(nameBytes.length),
        ...u16(0),
      ]);
      localParts.push(localHeader, nameBytes, data);

      const centralHeader: Uint8Array = new Uint8Array([
        ...u32(0x02014b50),
        ...u16(20),
        ...u16(20),
        ...u16(0),
        ...u16(0),
        ...u16(0),
        ...u16(0),
        ...u32(crc),
        ...u32(data.length),
        ...u32(data.length),
        ...u16(nameBytes.length),
        ...u16(0),
        ...u16(0),
        ...u16(0),
        ...u16(0),
        ...u32(0),
        ...u32(offset),
      ]);
      centralParts.push(centralHeader, nameBytes);
      offset += localHeader.length + nameBytes.length + data.length;
    });

    const centralSize = centralParts.reduce((sum, part) => sum + part.length, 0);
    const endRecord: Uint8Array = new Uint8Array([
      ...u32(0x06054b50),
      ...u16(0),
      ...u16(0),
      ...u16(files.length),
      ...u16(files.length),
      ...u32(centralSize),
      ...u32(offset),
      ...u16(0),
    ]);
    const zipParts: Uint8Array[] = [...localParts, ...centralParts, endRecord];
    const zipSize = zipParts.reduce((sum, part) => sum + part.length, 0);
    const zipBytes: Uint8Array = new Uint8Array(zipSize);
    let zipOffset = 0;
    zipParts.forEach((part) => {
      zipBytes.set(part, zipOffset);
      zipOffset += part.length;
    });
    const zipBuffer = zipBytes.buffer.slice(
      zipBytes.byteOffset,
      zipBytes.byteOffset + zipBytes.byteLength,
    ) as ArrayBuffer;
    const blob = new Blob([zipBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `invoices-${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [loadInvoicesPage]);

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <PageHeader
          title="Invoices"
          subtitle="Every invoice issued for this venue."
        />
        <SearchHeaderContainer
          search={search}
          setSearch={setSearch}
          dateRangeConfig={{
            value: dateRange ?? undefined,
            onChange: (v: any) => setDateRange(v),
            fullWidth: false,
          }}
          selectConfig={{
            options: [
              { label: "All", value: "all" },
              // Matches the label the table shows for these rows.
              {
                label: `${businessDisplayName} (Business)`,
                value: BUSINESS_SCOPE_VALUE,
              },
              ...branches.map((branch) => ({
                label: branch.name || "Unknown Branch",
                value: branch.id,
              })),
            ],
            value: selectValue,
            onChange: (v: any) => {
              setSelectValue(v);
            },
            placeholder: "Select branch",
            label: "Select Branch",
            size: "small",
            searchable: false,
            clearable: true,
            multiSelect: false,
            sx: {
              backgroundColor: COLORS.WhiteBackground,
            },
          }}
          selectConfig2={{
            options: [
              { label: "All", value: "all" },
              { label: "Paid", value: "PAID" },
              { label: "Partially Paid", value: "PARTIALLY_PAID" },
              { label: "PENDING", value: "PENDING" },
              { label: "Cancelled", value: "CANCELLED" },
            ],
            value: statusFilter,
            onChange: (v: any) => setStatusFilter(v),
            placeholder: "Status",
            label: "Status",
            size: "small",
            searchable: false,
            clearable: true,
            sx: { width: 180 },
          }}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.MainColor,
              startIcon: <AddIcon />,
              onClick: () => {
                setEditingRow(null);
                setInvoiceFormUnitId("all");
                setAddOpen(true);
              },
              textColor: COLORS.WhiteText,
              minHeight: 36,
              maxHeight: 36,
              sx: {
                width: { xs: "96%", sm: 36 },
                minWidth: { sm: 36 },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
            {
              label: "Export XLSX",
              onClick: (reportProgress) => exportXlsx(reportProgress),
              startIcon: <FileDownloadIcon />,
              variant: "contained",
              bgColor: COLORS.MainColor,
              textColor: "#fff",
              showExportProgress: true,
              sx: {
                width: { xs: "96%", sm: "auto" },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
          ]}
        />

        <CustomTable<any>
          filters={{
            page: 0,
            size: INVOICES_PAGE_SIZE,
            search: debouncedSearch,
            status: statusFilter,
            scope: String(selectValue ?? ""),
            dateRange: JSON.stringify(dateRange ?? null),
          }}
          refreshSignal={invoicesRefreshKey}
          fields={[
            { key: "Invoice_number", header: "Invoice #" },
            { key: "client_name", header: "Client Name" },
            { key: "mobile", header: "Phone Number" },
            { key: "branch", header: "Branch" },
            { key: "type", header: "Type" },
            {
              key: "status",
              header: "Status",
              render: (row: any) => {
                const raw = String(row.status || row.rawStatus || "").toUpperCase();
                const isPending = raw === "PENDING";
                const isPaid = raw === "PAID" || raw === "APPROVED";
                const isCancelled =
                  raw === "CANCELLED" ||
                  raw === "VOID" ||
                  raw === "VOIDED" ||
                  raw === "REJECTED";
                const bg = isPending
                  ? COLORS.MainColor
                  : isPaid
                  ? "#4caf50"
                  : isCancelled
                  ? COLORS.RED
                  : "#ff9800"; // orange for others
                const color = "#fff";
                
                // Normalize VOID/VOIDED to CANCELLED for display
                let displayStatus = raw;
                if (raw === "VOID" || raw === "VOIDED") {
                  displayStatus = "CANCELLED";
                }
                
                const label = displayStatus
                  ? displayStatus
                      .toLowerCase()
                      .split(/[_\s-]+/)
                      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                      .join(" ")
                  : "Status";
                return (
                  <span
                    style={{
                      display: "inline-block",
                      padding: "2px 10px",
                      borderRadius: 12,
                      backgroundColor: bg,
                      color,
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      textTransform: "capitalize",
                    }}
                  >
                    {label}
                  </span>
                );
              },
            },
            {
              key: "invoice_date",
              header: "Appointment Date",
              render: (row: any) => {
                const raw =
                  row?.invoice_date ||
                  row?.appointment?.start_at ||
                  row?.appointment?.startAt ||
                  row?.dueAt ||
                  row?.due_at ||
                  "";
                return raw ? formatTableDate(raw) : "-";
              },
            },
            {
              key: "created_at",
              header: "Created At",
              render: (row: any) => {
                const dateStr = row.created_at || row.createdAt;
                if (!dateStr) return "-";
                return formatTableDateTime(dateStr);
              },
            },
            {
              key: "paid_at",
              header: "Paid At",
              render: (row: any) => {
                const dateStr = row.paid_at || row.paidAt;
                if (!dateStr) return "-";
                return formatTableDateTime(dateStr);
              },
            },
            { key: "services", header: "Services" },
            {
              key: "products",
              header: "Products",
              render: (row: any) => {
                const entries: string[] = Array.isArray(row?.productEntries)
                  ? row.productEntries
                  : [];
                if (entries.length === 0) return "N/A";

                const shown = entries.slice(0, PRODUCTS_SHOWN_IN_CELL);
                const hidden = entries.length - shown.length;
                const label =
                  shown.join(", ") + (hidden > 0 ? ` +${hidden} more` : "");

                return (
                  <Tooltip
                    // Only worth a tooltip when the cell is hiding something.
                    title={hidden > 0 ? entries.join(", ") : ""}
                  >
                    <span>{label}</span>
                  </Tooltip>
                );
              },
            },
            { key: "net_amount", header: "Total" },
            { key: "paid_amount", header: "Paid" },
            { key: "remaining_amount", header: "Remaining" },
          ]}
          actions={wiredActions}
          fetchFunc={fetchInvoicesTablePage}
        />

        <Box
          sx={{
            backgroundColor: COLORS.WhiteBackground,
            borderRadius: "0 0 10px 10px",
            padding: "16px",
            marginTop: "-10px",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
            mb: 3,
          }}
        >
          <CustomLabel
            text="Total Paid Amount:"
            sx={{ color: COLORS.typographyColor }}
          />
          <CustomLabel
            text={new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(
              // Summed by the server over every invoice matching the filters.
              Number(invoicesSummary?.paidAmount) || 0
            )}
            fontWeight="bold"
            sx={{ color: COLORS.MainColor }}
          />
        </Box>

        <GenericFormDialog
          open={addOpen}
          onClose={() => {
            setAddOpen(false);
            setInvoiceFormUnitId("");
          }}
          title="Add Invoice"
          fields={addInvoiceFormFields}
          initialValues={{
            unitId: "all",
            vat: "0",
            discount: "0",
            paymentMethod: "cash",
            status: "PENDING",
          }}
          onValuesChange={(values) => {
            if (!addOpen) return;
            const nextUnit = String(values?.unitId || "");
            if (nextUnit !== invoiceFormUnitId) {
              setInvoiceFormUnitId(nextUnit);
            }
          }}
          onSubmit={async (values) => {
            try {
              const selectedUnitScope = String(values.unitId || "all");
              const unitIdToUse =
                selectedUnitScope !== "all" ? selectedUnitScope : "";

              const normalizedItems = Array.isArray(values.items)
                ? values.items
                : [];

              if (normalizedItems.some(isInvoiceProductMissingSelection)) {
                toast.error(
                  "Choose a product or delete the product row before saving the invoice."
                );
                return;
              }

              const services = normalizedItems
                .filter((item: any) => (item.itemType || "SERVICE") !== "PRODUCT")
                .map((item: any) => ({
                  id: item.serviceId || String(item.service),
                  name: item.serviceName || item.name || item.serviceLabel || "Service",
                  price: Number(item.unitCost),
                }));

              const products = normalizedItems
                .filter((item: any) => (item.itemType || "SERVICE") === "PRODUCT")
                .map((item: any) => ({
                  id: item.serviceId || String(item.service),
                  name: item.serviceName || item.name || item.serviceLabel || "Product",
                  price: Number(item.unitCost),
                  qty: Number(item.quantity) || 1,
                  businessId: String(venueId),
                }));

              const discountPercent = Number(values.discount) || 0;

              const payload: any = {
                clientOrgUserId: String(values.clientName),
                businessId: String(venueId),
                currency: "USD",

                notes: values.notes,

                services,
                products,
                items: [],

                promoCode: values.promoCode || undefined,
                promoType: "PERCENT" as "PERCENT",
                promoValue: discountPercent,
              };
              if (unitIdToUse) {
                payload.unitId = String(unitIdToUse);
              }
              console.log("payload: ", payload);
              await createQuotation(payload);
              await fetchInvoices();
              setAddOpen(false);
              setInvoiceFormUnitId("");
            } catch (error) {
              console.error("Error saving invoice:", error);
            }
          }}
        /><GenericFormDialog
  open={editOpen}
  onClose={() => setEditOpen(false)}
  title={(() => {
    const invoiceNb = pickNonEmptyString(
      editingRow?.invoice?.Invoice_number,
      editingRow?.invoice?.invoice_number,
      editingRow?.Invoice_number,
      editingRow?.invoice_number,
      editingRow?.invoice?.id,
      editingRow?.invoice?.id && `#${editingRow.invoice.id}`,
      editingRow?.id
    );
    return invoiceNb ? `Edit Invoice #${invoiceNb}` : "Edit Invoice";
  })()}
  fields={formFields}
  initialValues={{
    clientName: resolveInvoiceClientId(editingRow),
    email: resolveInvoiceClientEmail(editingRow),
    phoneNumber: resolveInvoiceClientPhone(editingRow),
    invoice_date: (() => {
      const date =
        editingRow?.invoice?.createdAt ||
        editingRow?.invoice?.created_at ||
        editingRow?.created_at ||
        editingRow?.invoice?.invoice_date ||
        editingRow?.invoice_date ||
        editingRow?.appointment?.start_at;
      if (!date) return "";
      const dateObj = new Date(date);
      return dateObj.toISOString().split("T")[0];
    })(),
    status: resolveStatusFormValue(
      pickNonEmptyString(
        editingRow?.invoice?.status,
        editingRow?.status,
        editingRow?.invoice?.status_name,
        editingRow?.invoice?.status_label
      ),
      editingRow?.invoice?.status_id ??
        editingRow?.status_id ??
        editingRow?.invoice?.status?.id ??
        editingRow?.status?.id
    ),
    paymentMethod: "cash",
    notes: editingRow?.invoice?.notes || editingRow?.notes || "",
    items: (() => {
      const canonicalItems =
        (Array.isArray(editingRow?.invoice?.items) &&
          editingRow.invoice.items.length &&
          editingRow.invoice.items) ||
        (Array.isArray(editingRow?.items) &&
          editingRow.items.length &&
          editingRow.items) ||
        null;

      if (canonicalItems) {
        const invoicePromoType =
          editingRow?.promoType ||
          editingRow?.promo_type ||
          editingRow?.invoice?.promoType ||
          editingRow?.invoice?.promo_type ||
          "";
        const invoicePromoValue =
          pickFiniteNumber(
            editingRow?.promoValue,
            editingRow?.promo_value,
            editingRow?.invoice?.promoValue,
            editingRow?.invoice?.promo_value
          ) ?? 0;

        return canonicalItems.map((item: any, index: number) => {
          const itemType =
            String(item?.metadata?.type || item?.type || "SERVICE").toUpperCase() ===
            "PRODUCT"
              ? "PRODUCT"
              : "SERVICE";
          const quantity = Number(item?.qty ?? item?.quantity ?? 1) || 1;
          const unitCost =
            Number(
              item?.unitPrice ??
                item?.unit_price ??
                item?.unitCost ??
                item?.amount ??
                item?.price ??
                0
            ) || 0;
          const totalPriceRaw = pickFiniteNumber(
            item?.totalPrice ??
              item?.total_price ??
              item?.net_amount ??
              item?.total
          );
          const totalPrice =
            totalPriceRaw !== undefined
              ? totalPriceRaw
              : calculateInvoiceItemTotal(
                  quantity,
                  unitCost,
                  Number(item?.discount) || 0
                );
          const itemId =
            itemType === "PRODUCT"
              ? item?.metadata?.productId ?? item?.productId ?? item?.product_id
              : item?.metadata?.serviceId ?? item?.serviceId ?? item?.service_id;

          return {
            id: index + 1,
            service: itemId ? String(itemId) : item?.description || "",
            serviceId: itemId ? String(itemId) : undefined,
            serviceLabel: String(item?.description || item?.name || ""),
            quantity,
            unitCost,
            discount: resolveInvoiceItemDiscountPercent(
              item,
              quantity,
              unitCost,
              totalPrice,
              invoicePromoType,
              invoicePromoValue
            ),
            totalPrice,
            itemType,
            metadata: item?.metadata || {},
          } as InvoiceItem;
        });
      }

      const productsSource =
        editingRow?.products ||
        editingRow?.invoice?.products ||
        editingRow?.metadata?.products ||
        [];
      const productItems: InvoiceItem[] = Array.isArray(productsSource)
        ? productsSource.map((p: any, idx: number) => {
            const qty = Number(p?.qty ?? p?.quantity ?? 1) || 1;
            const price =
              Number(p?.price ?? p?.unitPrice ?? p?.unit_price ?? 0) || 0;
            const totalPrice =
              pickFiniteNumber(
                p?.net_amount,
                p?.netAmount,
                p?.totalPrice,
                p?.total_price,
                p?.total
              ) ?? Math.round(qty * price * 100) / 100;
            const id =
              p?.id ??
              p?._id ??
              p?.productId ??
              p?.product_id ??
              `${idx + 1}`;
            return {
              id: idx + 1 + 1000,
              service: String(id),
              serviceId: String(id),
              serviceLabel: String(
                p?.name ?? p?.title ?? p?.label ?? p?.description ?? "Product"
              ),
              quantity: qty,
              unitCost: price,
              discount: resolveInvoiceItemDiscountPercent(
                p,
                qty,
                price,
                totalPrice
              ),
              totalPrice,
              itemType: "PRODUCT",
              metadata: p?.metadata || {},
            } as InvoiceItem;
          })
        : [];

      if (Array.isArray(editingRow?.details) && editingRow.details.length) {
        const items = editingRow.details.map((s: any, index: number) => {
          const detailServiceId = s.service_id ?? s.serviceId ?? s.serviceID;
          const normalizedServiceId =
            detailServiceId !== undefined && detailServiceId !== null
              ? String(detailServiceId)
              : undefined;

          let matchedService = services.find(
            (srv) =>
              String(srv.id || srv._id || srv.service_id || "") ===
              String(normalizedServiceId || "")
          );

          if (!matchedService && s.title) {
            matchedService = services.find((srv) => {
              const serviceNumber = srv.title
                ? String(srv.title).match(/^\d+/)?.[0]
                : null;
              const detailTitle = String(s.title).trim();
              return (
                serviceNumber === detailTitle || srv.title === detailTitle
              );
            });
          }

          const matchedServiceId =
            matchedService?.id ||
            matchedService?._id ||
            matchedService?.service_id;

          const resolvedServiceId =
            matchedServiceId || normalizedServiceId || undefined;

          const unitCost =
            s.amount ??
            s.unitCost ??
            s.unit_price ??
            s.price ??
            matchedService?.price ??
            0;

          return {
            id: index + 1,
            service: resolvedServiceId || detailServiceId || s.title || "",
            serviceId: resolvedServiceId,
            quantity: s.quantity ?? s.qty ?? 1,
            unitCost: Number(unitCost) || 0,
            discount: Number(s.discount) || 0,
            totalPrice: Number(s.net_amount) || Number(unitCost) || 0,
            itemType: "SERVICE",
            metadata: s?.metadata || {},
          };
        });
        return [...items, ...productItems];
      }

      const singleService = editingRow?.service;
      if (singleService) {
        const serviceUnitCost = Number(
          singleService?.price ??
          singleService?.unit_price ??
          editingRow?.servicePrice ??
          editingRow?.serviceprice ??
          0
        );
        const serviceItems = [
          {
            id: 1,
            service: singleService.id || singleService.name || "",
            serviceId: singleService.id,
            quantity: 1,
            unitCost: serviceUnitCost,
            discount: 0,
            totalPrice: serviceUnitCost,
            itemType: "SERVICE",
            metadata: {},
          },
        ];
        return [...serviceItems, ...productItems];
      }

      return productItems;
    })(),
    invoice_number:
      editingRow?.invoice?.Invoice_number ||
      editingRow?.invoice?.invoice_number ||
      editingRow?.Invoice_number ||
      editingRow?.invoice_number,
  appointment_id:
      editingRow?.appointment?.id ||
      editingRow?.invoice?.appointment_id ||
      editingRow?.appointment_id,
  net_amount:
      editingRow?.invoice?.amount ||
      editingRow?.invoice?.net_amount ||
      editingRow?.net_amount,
  total_amount:
      editingRow?.invoice?.amount ||
      editingRow?.invoice?.total_amount ||
      editingRow?.total_amount,
    vat: String(
      editingRow?.invoice?.tax ??
        editingRow?.tax ??
        editingRow?.invoice?.Vat ??
        editingRow?.Vat ??
        0
    ),
  discount: String(
      editingRow?.invoice?.discount || editingRow?.discount || 0
    ),
    promoDiscount: String(
      editingRow?.promoDiscount ||
        editingRow?.promo_discount ||
        editingRow?.invoice?.promoDiscount ||
        editingRow?.invoice?.promo_discount ||
        0
    ),
    promoValue: String(
      editingRow?.promoValue ||
        editingRow?.promo_value ||
        editingRow?.invoice?.promoValue ||
        editingRow?.invoice?.promo_value ||
        0
    ),
    promoType:
      editingRow?.promoType ||
      editingRow?.promo_type ||
      editingRow?.invoice?.promoType ||
      editingRow?.invoice?.promo_type ||
      "",
}}
  onSubmit={async (values) => {
    const normalizeStatus = (status: any) =>
      typeof status === "string" ? status.trim().toUpperCase() : "";
    const pickNumber = (...vals: any[]) => {
      for (const v of vals) {
        const n = Number(v);
        if (Number.isFinite(n)) return n;
      }
      return 0;
    };

    try {
      if (!editingRow) {
        toast.error("No invoice data available.");
        return;
      }

      const invoiceId = editingRow?.invoice?.id || editingRow?.id;
      if (!invoiceId) {
        toast.error("Invoice ID not found.");
        return;
      }

      const normalizedItems = Array.isArray(values.items) ? values.items : [];

      if (normalizedItems.some(isInvoiceProductMissingSelection)) {
        toast.error(
          "Choose a product or delete the product row before saving the invoice."
        );
        return;
      }

      const lineItems = normalizedItems.map((item: any) => ({
        quantity: Number(item.quantity),
        amount: Number(item.unitCost),
        discount: getInvoiceItemDiscountAmount(item),
        discountPercent: Number(item.discount || 0),
        net_amount: Number(item.totalPrice),
        itemType: item.itemType || "SERVICE",
        serviceId: item.serviceId || String(item.service),
        serviceLabel: item.serviceLabel || "",
        metadata: item.metadata || {},
      }));

      const totalAmount = lineItems.reduce(
        (sum: number, item: any) => sum + Number(item.net_amount || 0),
        0
      );
      const discountPercent = Number(values.discount) || 0;
      const discountAmount =
        Math.round(((totalAmount * discountPercent) / 100) * 100) / 100;
      const amountAfterDiscount = Math.max(0, totalAmount - discountAmount);
      const vatPercent = Number(values.vat) || 0;
      const vatAmount =
        Math.round(((amountAfterDiscount * vatPercent) / 100) * 100) / 100;
      const netAmount = Math.round((amountAfterDiscount + vatAmount) * 100) / 100;

      const totalFromApi = pickNumber(
        editingRow?.invoice?.total_amount,
        editingRow?.total_amount,
        editingRow?.invoice?.total,
        editingRow?.total,
        editingRow?.invoice?.net_amount,
        editingRow?.net_amount,
        netAmount
      );
      let paidAmount = pickNumber(
        editingRow?.invoice?.paid_amount,
        editingRow?.invoice?.paidAmount,
        editingRow?.invoice?.total_paid,
        editingRow?.paid_amount,
        editingRow?.paid,
        editingRow?.total_paid
      );
      const refundedAmount = pickNumber(
        editingRow?.invoice?.refunded_amount,
        editingRow?.invoice?.refundedAmount,
        editingRow?.invoice?.total_refunded,
        editingRow?.invoice?.refund_amount,
        editingRow?.refunded_amount,
        editingRow?.total_refunded,
        editingRow?.refund_amount
      );
      const rawRemaining = pickNumber(
        editingRow?.invoice?.remaining_amount,
        editingRow?.invoice?.remainingAmount,
        editingRow?.invoice?.balance,
        editingRow?.invoice?.outstanding,
        editingRow?.remaining_amount,
        editingRow?.balance,
        editingRow?.outstanding
      );
      if (paidAmount <= 0 && totalFromApi > 0 && rawRemaining >= 0) {
        const inferredPaid = totalFromApi - rawRemaining;
        if (Number.isFinite(inferredPaid) && inferredPaid > 0) {
          paidAmount = inferredPaid;
        }
      }
      if (paidAmount <= 0 && netAmount > 0 && rawRemaining >= 0) {
        const inferredPaid = netAmount - rawRemaining;
        if (Number.isFinite(inferredPaid) && inferredPaid > 0) {
          paidAmount = inferredPaid;
        }
      }
      const refundableAmount = Math.max(paidAmount - refundedAmount, 0);

      const computedRemaining = Math.max(
        (totalFromApi || netAmount) - paidAmount + refundedAmount,
        0
      );

      const payCandidates = [rawRemaining, computedRemaining, netAmount].filter(
        (v) => Number.isFinite(v) && v > 0
      );
      const minPositive = payCandidates.length ? Math.min(...payCandidates) : 0;
      const amountToPay = rawRemaining > 0 ? rawRemaining : minPositive;

      const amountToRefund = paidAmount > 0 ? paidAmount : Math.max(netAmount, 0);

      const currentStatus = normalizeStatus(
        editingRow?.invoice?.status ||
          editingRow?.status ||
          editingRow?.invoice?.status_name ||
          editingRow?.status_name ||
          "PENDING"
      );
      const requestedStatus = normalizeStatus(
        values.status || currentStatus || "PENDING"
      );

      if (
        currentStatus === "PENDING" &&
        requestedStatus !== "PENDING" &&
        requestedStatus !== "PAID"
      ) {
        toast.info("Pending invoices can only be marked as Paid here.");
        return;
      }

      const transition = `${currentStatus}->${requestedStatus}`;
      if (!ALLOWED_STATUS_TRANSITIONS.has(transition)) {
        toast.error(
          `Cannot change invoice status from ${currentStatus} to ${requestedStatus}.`
        );
        return;
      }

      if (currentStatus === "CANCELLED" && requestedStatus !== "CANCELLED") {
        toast.info("Cancelled invoices cannot be changed here.");
        return;
      }
      if (requestedStatus === "CANCELLED" && currentStatus !== "CANCELLED") {
        toast.info("Cancellation is handled in the invoices list, not here.");
        return;
      }
      if (
        requestedStatus !== "PAID" &&
        requestedStatus !== "UNPAID" &&
        requestedStatus !== "CANCELLED" &&
        requestedStatus !== currentStatus
      ) {
        toast.info("Only Pay or Unpay is supported in this edit dialog.");
        return;
      }

      const needsPayment =
        requestedStatus === "PAID" && currentStatus !== "PAID";
      const needsRefundToUnpaid =
        (currentStatus === "PAID" ||
          currentStatus === "PARTIALLY_PAID" ||
          currentStatus === "PARTIALLY_REFUNDED") &&
        requestedStatus === "UNPAID";
      const needsRefund = needsRefundToUnpaid;

      if (needsPayment && amountToPay <= 0) {
        toast.error("Nothing to pay. Invoice already settled or total is zero.");
        return;
      }

      const refundAmountToUse = refundableAmount;

      if (needsRefund && refundAmountToUse <= 0) {
        toast.error("Nothing to refund for this invoice.");
        return;
      }

      const branchSelection =
        toUnitId(selectValue) ||
        editingRow?.unit?.id ||
        editingRow?.unitId ||
        editingRow?.unit_id ||
        editingRow?.invoice?.unitId ||
        editingRow?.invoice?.unit_id ||
        editingRow?.unit?.id ||
        null;

      const branchRecord = branchSelection
        ? branches.find((b) => String(b.id) === String(branchSelection))
        : undefined;

      const businessIdToUse =
        editingRow?.business?.id ||
        editingRow?.invoice?.business_id ||
        editingRow?.business_id ||
        editingRow?.businessId ||
        branchRecord?.business_id ||
        branchRecord?.id ||
        editingRow?.business?.id ||
        venueId;

      const unitIdToUse =
        branchSelection ||
        editingRow?.unit?.id ||
        editingRow?.unitId ||
        editingRow?.unit_id ||
        editingRow?.invoice?.unitId ||
        editingRow?.invoice?.unit_id ||
        venueId;

      const existingMetadata = getInvoiceMetadata(editingRow);
      const existingMetadataClient = getInvoiceMetadataClient(editingRow);
      const clientMetadata = {
        ...existingMetadataClient,
        name:
          pickNonEmptyString(
            existingMetadataClient?.name,
            resolveInvoiceClientName(editingRow)
          ) || null,
        email:
          pickNonEmptyString(existingMetadataClient?.email, values.email) ||
          null,
        phone:
          pickNonEmptyString(
            existingMetadataClient?.phone,
            existingMetadataClient?.mobile,
            values.phoneNumber
          ) || null,
      };

      const itemsPayload = lineItems.map((item: any) => {
        const isProduct = item.itemType === "PRODUCT";
        return {
          description: item.serviceLabel || (isProduct ? "Product" : "Service"),
          qty: Number(item.quantity) || 1,
          unitPrice: Number(item.amount) || 0,
          discount: Number(item.discount || 0),
          metadata: {
            ...(item.metadata || {}),
            type: isProduct ? "PRODUCT" : "SERVICE",
            discountPercent: Number(item.discountPercent || 0),
            ...(isProduct
              ? {
                  productId: item.serviceId,
                  businessId: businessIdToUse,
                  stockDirection: "OUT",
                }
              : { serviceId: item.serviceId }),
          },
        };
      });

      const payload = {
        userId: values.clientName, // Billing user ID
        currency: "USD",
        discount: discountPercent,
        tax: vatPercent,
        issuedAt: values.invoice_date,
        sourceReferenceId:
          values.appointment_id ||
          editingRow?.invoice?.appointment_id ||
          editingRow?.appointment_id ||
          null,
        metadata: {
          ...existingMetadata,
          client: clientMetadata,
          clientOrgUserId: values.clientName, // CORE client UUID
          businessId: businessIdToUse, // BACKEND business (venueId in FE)
          unitId: unitIdToUse, // BACKEND unit (branch in FE)
          notes: values.notes,
        },
        items: itemsPayload,
      };

      const skipUpdate = needsRefund;
      let updateResponse: any = null;

      if (!skipUpdate) {
        updateResponse = await updateQuotation(Number(invoiceId), payload);
      }

      if (needsPayment) {
        const updatedAmountToPay = pickNumber(
          updateResponse?.updated?.balance,
          updateResponse?.balance,
          updateResponse?.updated?.total,
          updateResponse?.total,
          amountToPay
        );
        await payInvoice(String(invoiceId), {
          amount: updatedAmountToPay,
          method: "CASH",
          provider: "MANUAL",
          status: "SUCCESS",
        });
      } else if (needsRefund) {
        await refundInvoice(String(invoiceId), {
          amount: refundAmountToUse,
          status: "SUCCESS",
        });
        // Reflect unpaid locally
        setEditingRow((prev: any) => {
          if (!prev) return prev;
          return {
            ...prev,
            status: "UNPAID",
            invoice: { ...prev.invoice, status: "UNPAID", status_name: "UNPAID" },
          };
        });
      }

      const actionMessage = needsPayment
        ? "Invoice fully paid."
        : needsRefundToUnpaid
        ? "Invoice marked unpaid."
        : "Invoice updated successfully!";

      toast.success(actionMessage);
      await fetchInvoices();
      setEditOpen(false);
      setEditingRow(null);
    } catch (error) {
      console.error("Error updating invoice:", error);
      // The backend rejects a mispriced free-time line with the correct figure
      // in the message — show it rather than a generic failure.
      toast.error(getErrorMessage(error, "Failed to update invoice."));
    }
  }}
/>

        <CustomDialog
          open={tableDialogOpen}
          onClose={() => setTableDialogOpen(false)}
          title="Invoice Line Items"
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <CustomButton
              label="add"
              iconButtonSize={40}
              startIcon={<AddIcon />}
              textColor={COLORS.WhiteText}
              onClick={() => {
                setInvoiceItems((items) => [
                  ...items,
                  {
                    id: items.length
                      ? Math.max(...items.map((it) => it.id)) + 1
                      : 1,
                    service: 30,
                    quantity: 1,
                    unitCost: 0,
                    discount: 0,
                    totalPrice: 0,
                  },
                ]);
              }}
            />
          </Box>
          <TableContainer
            component={Paper}
            sx={{
              mt: 2,
              width: "100%",
              overflowX: { xs: "auto", md: "visible" },
            }}
          >
            <Table size="small" sx={{ minWidth: 800, width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ px: 0.5, py: 0.5, fontSize: 13 }}>
                    Service
                  </TableCell>
                  <TableCell sx={{ px: 0.5, py: 0.5, fontSize: 13 }}>
                    Quantity
                  </TableCell>
                  <TableCell sx={{ px: 0.5, py: 0.5, fontSize: 13 }}>
                    Unit Cost
                  </TableCell>
                  <TableCell sx={{ px: 0.5, py: 0.5, fontSize: 13 }}>
                    Discount (%)
                  </TableCell>
                  <TableCell sx={{ px: 0.5, py: 0.5, fontSize: 13 }}>
                    Total Price
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoiceItems.map((item, idx) => (
                  <TableRow key={item.id}>
                    <TableCell sx={{ px: 0.5, py: 0.5 }}>
                      <Select
                        size="small"
                        value={item.service}
                        onChange={(e) => {
                          const newService = Number(e.target.value);
                          setInvoiceItems((items) =>
                            items.map((it, i) => {
                              if (i !== idx) return it;
                              const updated: InvoiceItem = {
                                ...it,
                                service: newService,
                              };
                              if (newService === 60) {
                                updated.quantity = 1;
                                updated.unitCost = 20;
                                updated.discount = 0;
                              }
                              if (newService === 90) {
                                updated.quantity = 1;
                                updated.unitCost = 30;
                                updated.discount = 0;
                              }
                              if (newService === 120) {
                                updated.quantity = 1;
                                updated.unitCost = 40;
                                updated.discount = 0;
                              }
                              const qty = Number(updated.quantity || 0);
                              const unit = Number(updated.unitCost || 0);
                              const subtotal = qty * unit;
                              const discountPercent = Number(
                                updated.discount || 0
                              );
                              const discountAmount =
                                (subtotal * discountPercent) / 100;
                              updated.totalPrice =
                                Math.round((subtotal - discountAmount) * 100) /
                                100;
                              return updated;
                            })
                          );
                        }}
                        sx={{ minWidth: 60 }}
                      >
                        <MenuItem value={60}></MenuItem>
                        <MenuItem value={90}></MenuItem>
                        <MenuItem value={120}></MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell sx={{ px: 0.5, py: 0.5 }}>
                      <CustomTextField
                        value={item.quantity}
                        type="number"
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          setInvoiceItems((items) =>
                            items.map((it, i) => {
                              if (i !== idx) return it;
                              const updated = {
                                ...it,
                                quantity: value,
                              } as InvoiceItem;
                              const qty = Number(updated.quantity || 0);
                              const unit = Number(updated.unitCost || 0);
                              const subtotal = qty * unit;
                              const discountPercent = Number(
                                updated.discount || 0
                              );
                              const discountAmount =
                                (subtotal * discountPercent) / 100;
                              updated.totalPrice =
                                Math.round((subtotal - discountAmount) * 100) /
                                100;
                              return updated;
                            })
                          );
                        }}
                        fullWidth
                        size="small"
                        sx={{ fontSize: 13, px: 0.5, py: 0.5 }}
                      />
                    </TableCell>
                    <TableCell sx={{ px: 0.5, py: 0.5 }}>
                      <CustomTextField
                        value={item.unitCost}
                        type="number"
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          setInvoiceItems((items) =>
                            items.map((it, i) => {
                              if (i !== idx) return it;
                              const updated = {
                                ...it,
                                unitCost: value,
                              } as InvoiceItem;
                              const qty = Number(updated.quantity || 0);
                              const unit = Number(updated.unitCost || 0);
                              const subtotal = qty * unit;
                              const discountPercent = Number(
                                updated.discount || 0
                              );
                              const discountAmount =
                                (subtotal * discountPercent) / 100;
                              updated.totalPrice =
                                Math.round((subtotal - discountAmount) * 100) /
                                100;
                              return updated;
                            })
                          );
                        }}
                        fullWidth
                        size="small"
                        sx={{ fontSize: 13, px: 0.5, py: 0.5 }}
                      />
                    </TableCell>
                    <TableCell sx={{ px: 0.5, py: 0.5 }}>
                      <CustomTextField
                        value={item.discount}
                        type="number"
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          setInvoiceItems((items) =>
                            items.map((it, i) => {
                              if (i !== idx) return it;
                              const updated = {
                                ...it,
                                discount: Math.min(100, Math.max(0, value)),
                              } as InvoiceItem;
                              const qty = Number(updated.quantity || 0);
                              const unit = Number(updated.unitCost || 0);
                              const subtotal = qty * unit;
                              const discountPercent = Number(
                                updated.discount || 0
                              );
                              const discountAmount =
                                (subtotal * discountPercent) / 100;
                              updated.totalPrice =
                                Math.round((subtotal - discountAmount) * 100) /
                                100;
                              return updated;
                            })
                          );
                        }}
                        fullWidth
                        size="small"
                        sx={{ fontSize: 13, px: 0.5, py: 0.5 }}
                        inputProps={{ step: 0.01, min: 0, max: 100 }}
                      />
                    </TableCell>
                    <TableCell sx={{ px: 0.5, py: 0.5 }}>
                      <CustomTextField
                        value={item.totalPrice}
                        type="number"
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          setInvoiceItems((items) =>
                            items.map((it, i) =>
                              i === idx ? { ...it, totalPrice: value } : it
                            )
                          );
                        }}
                        fullWidth
                        size="small"
                        sx={{ fontSize: 13, px: 0.5, py: 0.5 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CustomDialog>
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => {
            setConfirmOpen(false);
            setToDeleteId(null);
          }}
          title="Delete entry"
          message="Are you sure you want to delete this entry? This action cannot be undone."
          onConfirm={async () => {
            if (toDeleteId === null) return;
            try {
              //await deleteInvoice(String(toDeleteId));
              await fetchInvoices();
              setConfirmOpen(false);
              setToDeleteId(null);
            } catch (error) {
              console.error("Error deleting invoice:", error);
              setConfirmOpen(false);
              setToDeleteId(null);
            }
          }}
        />

        <CustomDialog
          open={paymentConfirmOpen}
          onClose={() => {
            setPaymentConfirmOpen(false);
            setToPayId(null);
          }}
          title="Confirm Payment"
        >
          <Box sx={{ p: 2 }}>
            <CustomLabel
              text="Are you sure you want to mark this invoice as paid?"
              sx={{ mb: 2 }}
            />
            {toPayId &&
              (() => {
                const invoice = invoices.find((inv) => inv.id === toPayId);
                if (invoice) {
                  return (
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        backgroundColor: "#f5f5f5",
                        borderRadius: 1,
                      }}
                    >
                      <Box sx={{ mb: 1 }}>
                        <CustomLabel
                          text="Invoice Number: "
                          fontWeight="bold"
                          sx={{ display: "inline" }}
                        />
                        <CustomLabel
                          text={invoice.Invoice_number || "N/A"}
                          sx={{ display: "inline" }}
                        />
                      </Box>
                      <Box sx={{ mb: 1 }}>
                        <CustomLabel
                          text="Client Name: "
                          fontWeight="bold"
                          sx={{ display: "inline" }}
                        />
                        <CustomLabel
                          text={invoice.client_name || "N/A"}
                          sx={{ display: "inline" }}
                        />
                      </Box>
                      <Box sx={{ mb: 1 }}>
                        <CustomLabel
                          text="Date: "
                          fontWeight="bold"
                          sx={{ display: "inline" }}
                        />
                        <CustomLabel
                          text={invoice.invoice_date || "N/A"}
                          sx={{ display: "inline" }}
                        />
                      </Box>
                      <Box>
                        <CustomLabel
                          text="Branch: "
                          fontWeight="bold"
                          sx={{ display: "inline" }}
                        />
                        <CustomLabel
                          text={invoice.branch || "N/A"}
                          sx={{ display: "inline" }}
                        />
                        <CustomTextField
                          type="number"
                          value={partialAmount}
                          onChange={(e) => setPartialAmount(e.target.value)}
                        />
                      </Box>

                      <CustomLabel
                        text={`Remaining Balance: $${
                          invoice?.net_amount || invoice?.total_amount || 0
                        }`}
                        sx={{ mb: 1, fontWeight: "bold" }}
                      />
                    </Box>
                  );
                }
                return null;
              })()}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <CustomButton
                label="Cancel"
                onClick={() => {
                  setPaymentConfirmOpen(false);
                  setToPayId(null);
                }}
                bgColor="#9e9e9e"
                textColor="#fff"
              />
              <CustomButton
  label="Partial Pay"
  disabled={!partialAmount || Number(partialAmount) <= 0}
  bgColor="#1976d2"
  textColor="#fff"
  onClick={async () => {
    if (!toPayId) {
      toast.error("No invoice selected");
      return;
    }

    const invoice = invoices.find((inv) => inv.id === toPayId);
    if (!invoice) {
      toast.error("Invoice not found");
      return;
    }

    const remaining = Number(
      invoice.remaining_amount ?? invoice.balance ?? 0
    );

    const entered = Number(partialAmount);

    if (entered <= 0) {
      toast.error("Enter a valid amount");
      return;
    }

    const amountToPay = entered >= remaining ? remaining : entered;

    try {
      await payInvoice(String(toPayId), {
        amount: amountToPay,
        method: "CASH",
        provider: "MANUAL",
        status: "SUCCESS",
      });

      toast.success(
        amountToPay === remaining
          ? "Invoice fully paid"
          : "Partial payment recorded"
      );

      await fetchInvoices();

      setPartialAmount("");
      setPaymentConfirmOpen(false);
      setToPayId(null);
    } catch (error) {
      console.error("Payment failed:", error);
      showErrorToast(getErrorMessage(error, "Payment failed"));
    }
  }}
/>

             <CustomButton
  label="Full Payment"
  bgColor="#2e7d32"
  textColor="#fff"
  onClick={async () => {
    if (!toPayId) return;

    const invoice = invoices.find((inv) => inv.id === toPayId);
    if (!invoice) {
      toast.error("Invoice not found");
      return;
    }

    const remaining = Number(
      invoice.remaining_amount ?? invoice.balance ?? 0
    );

    if (remaining <= 0) {
      toast.error("Invoice is already fully paid");
      return;
    }

    try {
      await payInvoice(String(toPayId), {
        amount: remaining, 
        method: "CASH",
        provider: "MANUAL",
        status: "SUCCESS",
      });

      toast.success("Invoice fully paid");

      await fetchInvoices();
      setPaymentConfirmOpen(false);
      setToPayId(null);
    } catch (error) {
      console.error("Full payment failed:", error);
      showErrorToast(getErrorMessage(error, "Full payment failed"));
    }
  }}
/>

            </Box>
          </Box>
        </CustomDialog>

        <CustomDialog
          open={cancelConfirmOpen}
          onClose={() => {
            setCancelConfirmOpen(false);
            setToCancelId(null);
            setCancelReason("");
          }}
          title="Cancel Invoice"
        >
          <Box sx={{ p: 2 }}>
            <CustomLabel
              text="Are you sure you want to cancel this invoice? This action will create a credit note."
              sx={{ mb: 2 }}
            />
            {toCancelId &&
              (() => {
                const invoice = invoices.find((inv) => inv.id === toCancelId);
                if (invoice) {
                  return (
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        backgroundColor: "#f5f5f5",
                        borderRadius: 1,
                      }}
                    >
                      <Box sx={{ mb: 1 }}>
                        <CustomLabel
                          text="Invoice Number: "
                          fontWeight="bold"
                          sx={{ display: "inline" }}
                        />
                        <CustomLabel
                          text={invoice.Invoice_number || "N/A"}
                          sx={{ display: "inline" }}
                        />
                      </Box>
                      <Box sx={{ mb: 1 }}>
                        <CustomLabel
                          text="Client Name: "
                          fontWeight="bold"
                          sx={{ display: "inline" }}
                        />
                        <CustomLabel
                          text={invoice.client_name || "N/A"}
                          sx={{ display: "inline" }}
                        />
                      </Box>
                      <Box sx={{ mb: 1 }}>
                        <CustomLabel
                          text="Date: "
                          fontWeight="bold"
                          sx={{ display: "inline" }}
                        />
                        <CustomLabel
                          text={invoice.invoice_date || "N/A"}
                          sx={{ display: "inline" }}
                        />
                      </Box>
                      <Box>
                        <CustomLabel
                          text="Branch: "
                          fontWeight="bold"
                          sx={{ display: "inline" }}
                        />
                        <CustomLabel
                          text={invoice.branch || "N/A"}
                          sx={{ display: "inline" }}
                        />
                      </Box>
                    </Box>
                  );
                }
                return null;
              })()}
            <CustomTextField
              label="Reason for Cancellation"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="Please provide a reason for cancelling this invoice..."
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <CustomButton
                label="Cancel"
                onClick={() => {
                  setCancelConfirmOpen(false);
                  setToCancelId(null);
                  setCancelReason("");
                }}
                bgColor="#9e9e9e"
                textColor="#fff"
              />
              <CustomButton
                label="Confirm Cancellation"
                onClick={async () => {
                  if (toCancelId === null) return;
                  try {
                    const invoice = invoices.find(
                      (inv) => inv.id === toCancelId
                    );

                    const amount =
                      invoice?.total_amount ||
                      invoice?.amount ||
                      invoice?.balance ||
                      0;

                    await cancelInvoice(String(toCancelId), {
                      amount: Number(amount),
                      reason: cancelReason.trim() || "No reason provided",
                    });
                    const updated = await getInvoiceById(String(toCancelId));
                    const updatedData = (updated as any).data || updated;

                    // Immediately reflect cancelled status in UI while backend propagates.
                    setSelectedInvoice(
                      updatedData
                        ? { ...updatedData, status: "CANCELLED" }
                        : { status: "CANCELLED" }
                    );
                    setInvoices((prev) =>
                      Array.isArray(prev)
                        ? prev.map((inv) => {
                            const id = getInvoiceId(inv);
                            return id && String(id) === String(toCancelId)
                              ? { ...inv, status: "CANCELLED", rawStatus: "CANCELLED" }
                              : inv;
                          })
                        : prev
                    );

                    console.log("Invoice cancelled:", toCancelId);
                    await fetchInvoices();
                    setCancelConfirmOpen(false);
                    setToCancelId(null);
                    setCancelReason("");
                  } catch (error) {
                    console.error("Error cancelling invoice:", error);
                    setCancelConfirmOpen(false);
                    setToCancelId(null);
                    setCancelReason("");
                  }
                }}
                bgColor="#d32f2f"
                textColor="#fff"
              />
            </Box>
          </Box>
        </CustomDialog>

        <CustomDialog
         paperSx={{ minWidth: 800 }}
          open={viewDialogOpen}
          onClose={() => {
            setViewDialogOpen(false);
            setSelectedInvoice(null);
          }}
          title="Invoice Details"
        >
          {selectedInvoice &&
            (() => {
              const invoiceDetails =
                selectedInvoice.invoice || selectedInvoice || {};
              const selectedMetadataClient =
                getInvoiceMetadataClient(selectedInvoice);
              const client =
                selectedInvoice.client ||
                invoiceDetails.client ||
                invoiceDetails.client_info ||
                selectedMetadataClient ||
                {};
              const unit =
                selectedInvoice.unit ||
                invoiceDetails.unit ||
                (invoiceDetails.metadata &&
                  typeof invoiceDetails.metadata === "object" &&
                  (invoiceDetails.metadata as any).unit) ||
                {};
              const createdAt =
                invoiceDetails.createdAt ||
                invoiceDetails.created_at ||
                selectedInvoice.createdAt ||
                invoiceDetails.issuedAt ||
                invoiceDetails.invoice_date;
              const invoiceItemsList =
                (Array.isArray(selectedInvoice.items) &&
                  selectedInvoice.items.length &&
                  selectedInvoice.items) ||
                (Array.isArray(invoiceDetails.items) &&
                  invoiceDetails.items.length &&
                  invoiceDetails.items) ||
                [];
              const isProductLine = (item: any) => {
                const type = String(item?.metadata?.type || item?.type || "")
                  .trim()
                  .toUpperCase();
                return (
                  type === "PRODUCT" ||
                  item?.metadata?.productId !== undefined ||
                  item?.metadata?.product_id !== undefined ||
                  item?.productId !== undefined ||
                  item?.product_id !== undefined
                );
              };
              const invoiceServiceItems = invoiceItemsList.filter(
                (item: any) => !isProductLine(item)
              );
              const invoiceProductItems = invoiceItemsList.filter(isProductLine);
              const servicesList =
                invoiceServiceItems.length > 0
                  ? invoiceServiceItems
                  : selectedInvoice.services ||
                    selectedInvoice.metadata?.services ||
                    invoiceDetails.services ||
                    invoiceDetails.metadata?.services ||
                    (selectedInvoice.service ||
                    invoiceDetails.service ||
                    selectedInvoice.metadata?.service ||
                    invoiceDetails.metadata?.service
                      ? [
                          selectedInvoice.service ||
                            invoiceDetails.service ||
                            selectedInvoice.metadata?.service ||
                            invoiceDetails.metadata?.service,
                        ]
                      : []);
              const productsList =
                invoiceProductItems.length > 0
                  ? invoiceProductItems
                  : selectedInvoice.products ||
                    invoiceDetails.products ||
                    selectedInvoice.metadata?.products ||
                    invoiceDetails.metadata?.products ||
                    [];
              const subtotalValue =
                invoiceDetails.subtotal ||
                invoiceDetails.total_amount ||
                invoiceDetails.total ||
                invoiceDetails.amount ||
                0;
              const netAmountValue =
                invoiceDetails.netAmount ||
                invoiceDetails.net_amount ||
                invoiceDetails.metadata?.netAmount ||
                invoiceDetails.total_amount ||
                invoiceDetails.total ||
                invoiceDetails.amount ||
                0;
              const emailValue =
                client.user?.email ||
                client.email ||
                selectedMetadataClient.email ||
                selectedInvoice.email ||
                invoiceDetails.email ||
                "N/A";

              return (
                <Box sx={{ p: 2 }}>
              <Box
                sx={{
                  mb: 2,
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 2,
                }}
              >
                <Box>
                  <CustomLabel
                    text="Invoice Number"
                    fontWeight="bold"
                    sx={{ mb: 0.5 }}
                  />
                  <CustomLabel
                    text={
                      invoiceDetails.invoiceId ||
                      invoiceDetails.Invoice_number ||
                      invoiceDetails.id ||
                      selectedInvoice.id ||
                      "N/A"
                    }
                  />
                </Box>
                <Box>
                  <CustomLabel
                    text="Status"
                    fontWeight="bold"
                    sx={{ mb: 0.5 }}
                  />
                  <CustomLabel
                    text={
                      invoiceDetails.status || selectedInvoice.status
                        ? String(
                            invoiceDetails.status || selectedInvoice.status
                          ).toUpperCase()
                        : "N/A"
                    }
                  />
                </Box>
                <Box>
                  <CustomLabel
                    text="Client Name"
                    fontWeight="bold"
                    sx={{ mb: 0.5 }}
                  />
                  <CustomLabel
                    text={
                      client.full_name ||
                      client.name ||
                      client.fullName ||
                      client.client_name ||
                      selectedMetadataClient.name ||
                      "N/A"
                    }
                  />
                </Box>
                <Box>
                  <CustomLabel
                    text="Phone Number"
                    fontWeight="bold"
                    sx={{ mb: 0.5 }}
                  />
                  <CustomLabel
                    text={
                      client.phone ||
                      client.mobile ||
                      client.contact_number ||
                      client.phone_number ||
                      selectedMetadataClient.phone ||
                      selectedMetadataClient.mobile ||
                      "N/A"
                    }
                  />
                </Box>
                <Box>
                  <CustomLabel
                    text="Email"
                    fontWeight="bold"
                    sx={{ mb: 0.5 }}
                  />
                  <CustomLabel text={emailValue} />
                </Box>
                <Box>
                  <CustomLabel
                    text="Branch"
                    fontWeight="bold"
                    sx={{ mb: 0.5 }}
                  />
                  <CustomLabel
                    text={
                      unit.name ||
                      branches.find(
                        (b) =>
                          String(b.id) ===
                          String(
                            unit?.id ||
                              selectedInvoice.metadata?.unitId ||
                              selectedInvoice.unitId ||
                              invoiceDetails.unitId ||
                              invoiceDetails.unit_id
                          )
                      )?.name ||
                      "N/A"
                    }
                  />
                </Box>
                <Box>
                  <CustomLabel
                    text="Payment Method"
                    fontWeight="bold"
                    sx={{ mb: 0.5 }}
                  />
                  <CustomLabel
                    text={String(
                      invoiceDetails.payment_method ||
                        invoiceDetails.method ||
                        invoiceDetails.paymentMethod ||
                        selectedInvoice.payment_method ||
                        selectedInvoice.method ||
                        "cash"
                    ).toUpperCase()}
                  />
                </Box>
                <Box>
                  <CustomLabel
                    text="Created Date"
                    fontWeight="bold"
                    sx={{ mb: 0.5 }}
                  />
                  <CustomLabel
                    text={
                      createdAt ? formatTableDate(createdAt) : "N/A"
                    }
                  />
                </Box>
              </Box>

              <Box sx={{ mt: 3, mb: 2 }}>
                <CustomLabel text="Services" fontWeight="bold" sx={{ mb: 1 }} />
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Service</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Discount</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                  <TableBody>
                    {(
                        servicesList
                      )
                        ?.filter(Boolean)
                        ?.map((service: any, idx: number) => {
                          const quantity = Number(
                            service?.quantity || service?.qty || 1
                          ) || 1;
                          const unitPrice =
                            Number(
                              service?.price ||
                                service?.unitPrice ||
                                service?.unit_price ||
                                service?.amount ||
                                0
                            ) || 0;
                          const totalPrice =
                            pickFiniteNumber(
                              service?.net_amount,
                              service?.netAmount,
                              service?.totalPrice,
                              service?.total_price,
                              service?.total
                            ) ?? unitPrice;
                          const discountPercent = resolveInvoiceItemDiscountPercent(
                            service,
                            quantity,
                            unitPrice,
                            totalPrice
                          );

                          return (
                            <TableRow key={idx}>
                              <TableCell>
                                {service?.name || service?.description || "N/A"}
                              </TableCell>
                              <TableCell align="right">{quantity}</TableCell>
                              <TableCell align="right">${unitPrice}</TableCell>
                              <TableCell align="right">
                                {discountPercent}%
                              </TableCell>
                              <TableCell align="right">${totalPrice}</TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {Array.isArray(productsList) && productsList.length > 0 ? (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <CustomLabel
                    text="Products"
                    fontWeight="bold"
                    sx={{ mb: 1 }}
                  />
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Product</TableCell>
                          <TableCell align="right">Quantity</TableCell>
                          <TableCell align="right">Unit Price</TableCell>
                          <TableCell align="right">Discount</TableCell>
                          <TableCell align="right">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {productsList.map((p: any, idx: number) => {
                          const qty = Number(p?.qty ?? p?.quantity ?? 1);
                          const price = Number(
                            p?.price ?? p?.unitPrice ?? p?.unit_price ?? 0
                          );
                          const total =
                            pickFiniteNumber(
                              p?.net_amount,
                              p?.netAmount,
                              p?.total,
                              p?.totalPrice,
                              p?.total_price
                            ) ?? qty * price;
                          const discountPercent =
                            resolveInvoiceItemDiscountPercent(
                              p,
                              qty,
                              price,
                              total
                            );
                          return (
                            <TableRow key={p?.id || idx}>
                              <TableCell>
                                {p?.name || p?.productName || p?.description || "N/A"}
                              </TableCell>
                              <TableCell align="right">{qty}</TableCell>
                              <TableCell align="right">${price}</TableCell>
                              <TableCell align="right">
                                {discountPercent}%
                              </TableCell>
                              <TableCell align="right">${total}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : null}

              <Box
                sx={{
                  mt: 3,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  alignItems: "flex-end",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    width: "300px",
                    justifyContent: "space-between",
                  }}
                >
                  <CustomLabel text="Subtotal:" fontWeight="bold" />
                  <CustomLabel
                    text={`$${
                      subtotalValue
                    }`}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    width: "300px",
                    justifyContent: "space-between",
                  }}
                >
                  <CustomLabel text="Discount:" fontWeight="bold" />
                  <CustomLabel
                    text={`${invoiceDetails.discount || selectedInvoice.discount || 0}%`}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    width: "300px",
                    justifyContent: "space-between",
                  }}
                >
                  <CustomLabel text="Tax/VAT:" fontWeight="bold" />
                  <CustomLabel
                    text={`${invoiceDetails.tax || selectedInvoice.tax || 0}%`}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    width: "300px",
                    justifyContent: "space-between",
                    borderTop: "2px solid #000",
                    pt: 1,
                  }}
                >
                  <CustomLabel
                    text="Net Amount:"
                    fontWeight="bold"
                    fontSize={16}
                  />
                  <CustomLabel
                    text={`$${
                      Number(invoiceDetails.amount ?? 0) === 0 &&
                      Number(invoiceDetails.balance ?? 0) === 0
                        ? 0
                        : netAmountValue
                    }`}
                    fontWeight="bold"
                    fontSize={16}
                    color={COLORS.MainColor}
                  />
                </Box>
              </Box>

              {(selectedInvoice.notes ||
                selectedInvoice.metadata?.notes ||
                invoiceDetails.notes ||
                invoiceDetails.metadata?.notes) && (
                <Box sx={{ mt: 3 }}>
                  <CustomLabel
                    text="Notes"
                    fontWeight="bold"
                    sx={{ mb: 0.5 }}
                  />
                  <CustomLabel
                    text={
                      selectedInvoice.notes ||
                      selectedInvoice.metadata?.notes ||
                      invoiceDetails.notes ||
                      invoiceDetails.metadata?.notes
                    }
                  />
                </Box>
              )}

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  mt: 3,
                  justifyContent: "flex-end",
                }}
              >
                <CustomButton
                  label="Issue Credit Note"
                  bgColor="#d32f2f"
                  textColor="#fff"
                  onClick={() => {
                    const id = getInvoiceId(selectedInvoice);

                    if (!id) {
                      toast.error("No invoice selected");
                      return;
                    }

                    console.log("Selected invoice ID:", id);
                    setNoteType("CREDIT_NOTE");
                    setNoteInvoiceId(id as any);
                    setViewDialogOpen(false);
                    setNoteOpen(true);
                  }}
                />

                {selectedInvoice.status !== "PAID" &&
                  selectedInvoice.status !== "CANCELLED" && (
                    <CustomButton
                      label="Issue Debit Note"
                      bgColor="#1976d2"
                      textColor="#fff"
                      onClick={() => {
                        const id = getInvoiceId(selectedInvoice);

                        if (!id) {
                          toast.error("No invoice selected");
                          return;
                        }

                        console.log("Selected invoice ID:", id);

                        setNoteType("DEBIT_NOTE");
                        setNoteInvoiceId(id as any);
                        setViewDialogOpen(false);
                        setNoteOpen(true);
                      }}
                    />
                  )}
                <CustomButton
                  label="Refund"
                  bgColor="#ff9800"
                  textColor="#fff"
                  onClick={() => {
  console.log("Refund clicked for invoice:", selectedInvoice);

  const invoice = selectedInvoice?.invoice || selectedInvoice;


  if (!invoice?.id) {
    toast.error("No invoice ID found");
    return;
  }

  // Calculate paid amount
  const paidAmount = Number(
    invoice.paid_amount !== undefined
      ? invoice.paid_amount
      : (invoice.amount || invoice.total || 0) - (invoice.balance || 0)
  );

  // Allow refund if there's any paid amount
  if (paidAmount <= 0) {
    toast.error("No paid amount available to refund");
    return;
  }

  setRefundInvoiceId(invoice.id);
  setRefundOpen(true);
}}

                />
                <CustomButton
                  label="Cancel"
                  bgColor="#9e9e9e"
                  textColor="#fff"
                  onClick={() => {
                    setViewDialogOpen(false);
                  }}
                />
              </Box>
            </Box>
              );
            })()}
        </CustomDialog>

        <GenericFormDialog
          open={noteOpen}
          onClose={() => {
            setNoteOpen(false);
            setNoteInvoiceId(null);
          }}
          title={
            noteType === "CREDIT_NOTE"
              ? "Create Credit Note"
              : "Create Debit Note"
          }
          fields={[
            {
              name: "amount",
              label: "Amount",
              type: "number",
              required: true,
            },
            {
              name: "reason",
              label: "Reason",
            },
          ]}
          disableSubmitUntilValid
          onSubmit={async (values) => {
            const invoiceIdForNote =
              noteInvoiceId || getInvoiceId(selectedInvoice);

            if (!invoiceIdForNote) {
              toast.error("No invoice selected");
              return;
            }
            const invoice = selectedInvoice?.invoice || selectedInvoice;


            if (
              noteType === "CREDIT_NOTE" &&
              Number(values.amount) > Number(invoice.total)
            ) {
              toast.error("Credit exceeds invoice total");
              return;
            }
            console.log("Creating note with values:", values);
            const payload = {
              invoiceId: invoiceIdForNote,
              amount: Number(values.amount),
              reason: values.reason,
            };

            try {
              if (noteType === "CREDIT_NOTE") {
                await createCreditNote(payload);
              } else {
                await createDebitNote(payload);
              }

              toast.success("Note created successfully");

              // Refresh invoice
              const updated = await getInvoiceById(String(invoiceIdForNote));
              setSelectedInvoice((updated as any).data || updated);

              // Refresh list
              await fetchInvoices();

              setNoteOpen(false);
              setNoteInvoiceId(null);
              setViewDialogOpen(false);
            } catch (err) {
              console.error(err);
              showErrorToast(getErrorMessage(err, "Failed to create note"));
            }
          }}
/>
      </Box>
<GenericFormDialog
  open={refundOpen}
  onClose={() => setRefundOpen(false)}
  title="Refund Invoice"
  fields={[
    {
      name: "amount",
      label: "Refund Amount",
      type: "number",
      required: true,
    },
    {
      name: "reason",
      label: "Refund Reason",
    },
  ]}
  disableSubmitUntilValid
  onSubmit={async (values) => {
    if (!refundInvoiceId) {
      toast.error("No invoice selected");
      return;
    }

    const invoice = selectedInvoice?.invoice || selectedInvoice;


    if (!invoice) {
      toast.error("Invoice data not loaded");
      return;
    }

    // Calculate paid amount
    const paidAmount = Number(
      invoice.paid_amount !== undefined
        ? invoice.paid_amount
        : (invoice.amount || invoice.total || 0) - (invoice.balance || 0)
    );

    // Allow refund if there's any paid amount
    if (paidAmount <= 0) {
      toast.error("No paid amount available to refund");
      return;
    }

    // Calculate already refunded amount
    const refundedAmount = Number(invoice.refunded_amount || invoice.refundedAmount || 0);
    
    // Calculate remaining refundable amount
    const refundableAmount = paidAmount - refundedAmount;

    if (Number(values.amount) > refundableAmount) {
      toast.error(`Refund exceeds available amount. Maximum refundable: $${refundableAmount.toFixed(2)}`);
      return;
    }

    try {
      await refundInvoice(String(refundInvoiceId), {
        amount: Number(values.amount),
        reason: values.reason,
        status: "SUCCESS",
      });

      toast.success("Refund processed successfully");

      const updated = await getInvoiceById(String(refundInvoiceId));
      setSelectedInvoice((updated as any).data || updated);

      await fetchInvoices();
      setRefundOpen(false);
      setViewDialogOpen(false);
    } catch (err) {
      console.error(err);
      showErrorToast(getErrorMessage(err, "Refund failed"));
    }
  }}
/>
    </Box>
  );
};

export default Invoices;
